import React, { useState, useRef, useEffect } from 'react';

const NODE_RADIUS = 16;
const GRID = 60;

// Junction types
const JUNCTION_TYPES = [
  { key: 'priority', label: '⬜ Priority' },
  { key: 'roundabout', label: '⭕ Roundabout' },
  { key: 'signals', label: '🚦 Signals' },
  { key: 'signals_roundabout', label: '🚦⭕ Signal Roundabout' },
];

function snap(v) { return Math.round(v / GRID) * GRID; }

function pointToLineDistance(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { dist: Math.sqrt((px - x1) ** 2 + (py - y1) ** 2), nearX: x1, nearY: y1 };
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const nearX = x1 + t * dx, nearY = y1 + t * dy;
  return { dist: Math.sqrt((px - nearX) ** 2 + (py - nearY) ** 2), nearX, nearY };
}

function getLinkSegments(src, tgt) {
  const midX = tgt.x, midY = src.y;
  return [
    { x1: src.x, y1: src.y, x2: midX, y2: midY },
    { x1: midX, y1: midY, x2: tgt.x, y2: tgt.y }
  ];
}

function siteBoxVirtualNode(siteBox) {
  if (!siteBox) return null;
  const cx = siteBox.x + siteBox.w / 2;
  const cy = siteBox.y + siteBox.h / 2;
  return { id: '__sitebox__', x: cx, y: cy, label: 'SITE', isSite: true, virtual: true };
}

// --- Gate-based junction turning counts -----------------------------------
// Replaces the old circle-cordon approach. A "gate" is a short line the user
// draws across a real approach road; coordinates are [lng, lat] pairs to
// match GeoJSON convention used throughout this app.

function gateCrossProduct(o, a, b) {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

function gateSegmentsIntersect(p1, p2, p3, p4) {
  const d1 = gateCrossProduct(p3, p4, p1);
  const d2 = gateCrossProduct(p3, p4, p2);
  const d3 = gateCrossProduct(p1, p2, p3);
  const d4 = gateCrossProduct(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
         ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

// Every point where routeCoords crosses one of `gates`, in polyline order.
// `side` is the raw geometric crossing direction (arbitrary sign) — used only
// for draw-time one-way suggestion, never for entry/exit role (see below).
function findGateCrossings(routeCoords, gates) {
  const crossings = [];
  for (let i = 0; i < routeCoords.length - 1; i++) {
    const a = routeCoords[i];
    const b = routeCoords[i + 1];
    for (const gate of gates) {
      if (gateSegmentsIntersect(a, b, gate.p1, gate.p2)) {
        const side = gateCrossProduct(gate.p1, gate.p2, a) > 0 ? 1 : -1;
        crossings.push({ gateId: gate.id, letter: gate.letter, idx: i, side, direction: gate.direction });
      }
    }
  }
  crossings.sort((x, y) => x.idx - y.idx);
  return crossings;
}

// Cumulative polyline distance (metres) between two crossing points, each
// identified by the index of the segment it falls on. Must include BOTH
// segments (idx1 and idx2 themselves), not just the ones strictly between
// them — a crossing on idx2 hasn't happened until you traverse that
// segment, so excluding it undercounts badly whenever the two crossings
// are on adjacent segments (e.g. a route that immediately doubles back at
// one vertex — exactly what a real U-turn or excursion looks like).
function polylineDistanceBetween(coords, idx1, idx2) {
  let dist = 0;
  const lo = Math.min(idx1, idx2), hi = Math.max(idx1, idx2);
  for (let i = lo; i <= hi; i++) {
    const a = coords[i], b = coords[i + 1];
    const dLng = (b[0] - a[0]) * Math.cos((a[1] + b[1]) * Math.PI / 360) * 111320;
    const dLat = (b[1] - a[1]) * 111320;
    dist += Math.sqrt(dLng * dLng + dLat * dLat);
  }
  return dist;
}

function pointDistanceMetres(a, b) {
  const dLng = (b[0] - a[0]) * Math.cos((a[1] + b[1]) * Math.PI / 360) * 111320;
  const dLat = (b[1] - a[1]) * 111320;
  return Math.sqrt(dLng * dLng + dLat * dLat);
}

function gateMidpoint(gate) {
  return [(gate.p1[0] + gate.p2[0]) / 2, (gate.p1[1] + gate.p2[1]) / 2];
}

function gatesCentroid(gates) {
  const mids = gates.map(gateMidpoint);
  return [
    mids.reduce((s, m) => s + m[0], 0) / mids.length,
    mids.reduce((s, m) => s + m[1], 0) / mids.length,
  ];
}

// Same-gate-twice is ambiguous on its own: a roundabout U-turn crosses the
// same gate going in and coming out with nothing else in between (a real,
// short loop AT this junction) — but a banned-turn workaround can *also*
// cross the same gate twice with nothing else in between, if the vehicle
// exits, travels well away to find a U-turn point, and re-enters via the
// same arm (that's really two separate passes through this junction, not
// one). The two look identical in the crossing sequence; what differs is
// how far the route actually travelled between the two crossings. Scaled
// per junction rather than one fixed guess for every roundabout: looping
// nearly all the way around a circulatory to re-emerge at the same arm is
// roughly the circumference, which for gates spread around a circle works
// out to several times the widest gate-to-gate distance — hence the x4
// multiplier, with a 400m floor for small/tight junctions.
function junctionLoopThreshold(gates) {
  const mids = gates.map(gateMidpoint);
  let maxDist = 0;
  for (let i = 0; i < mids.length; i++) {
    for (let j = i + 1; j < mids.length; j++) {
      const d = pointDistanceMetres(mids[i], mids[j]);
      if (d > maxDist) maxDist = d;
    }
  }
  return Math.max(400, maxDist * 4);
}

// Decomposes a route's ordered gate crossings into consecutive arm-to-arm
// movements, rather than collapsing the whole route to a single first/last
// pair. A route crossing gates [C, A, A, B], where the two A crossings are
// far apart, made TWO real movements through this junction: C->A, then
// (after travelling away and coming back — e.g. a banned-turn U-turn
// workaround further down the road) A->B. Each movement carries the FULL
// trip count: it's the same vehicles making each successive turn, and a
// diversion like this genuinely does load arm A twice — that's not
// double-counting, it's what actually happens. A same-gate pair close
// together (roundabout-scale, see junctionLoopThreshold) is instead kept
// as one real D->D movement.
function resolveRouteMovements(crossings, coords, maxLoopMetres) {
  const movements = [];
  for (let i = 0; i < crossings.length - 1; i++) {
    const from = crossings[i];
    const to = crossings[i + 1];

    if (from.gateId === to.gateId) {
      const dist = polylineDistanceBetween(coords, from.idx, to.idx);
      if (dist > maxLoopMetres) continue; // distant excursion, not a movement here
      // else: close together relative to this junction's size -> a real D->D movement
    }

    let flagged = false;
    // Declared one-way direction is a documentation/validation aid: if this
    // movement's observed role at a one-way gate contradicts what it's
    // declared to allow, still count the trip but flag it so the matrix UI
    // can surface a sanity-check warning.
    if (from.direction && from.direction !== 'two-way' && from.direction !== 'entry') flagged = true;
    if (to.direction && to.direction !== 'two-way' && to.direction !== 'exit') flagged = true;
    movements.push({ fromLetter: from.letter, toLetter: to.letter, flagged });
  }
  return movements;
}

function gateGeoBearing([lng1, lat1], [lng2, lat2]) {
  const dLng = (lng2 - lng1) * Math.cos((lat1 + lat2) * Math.PI / 360);
  const dLat = lat2 - lat1;
  return (Math.atan2(dLng, dLat) * 180 / Math.PI + 360) % 360;
}

// Builds the full arm x arm turning matrix, per-arm arrivals/departures
// totals (for seeding schematic link flow boxes), and a flagged-cell map —
// in one pass, shared by both call sites that need gate-crossing data.
function computeGateCrossings(gates, routes) {
  const letters = gates.map(g => g.letter);
  const matrix = {};
  const flags = {};
  const armArrivals = {};
  const armDepartures = {};
  for (const l of letters) {
    matrix[l] = {};
    flags[l] = {};
    for (const l2 of letters) { matrix[l][l2] = 0; flags[l][l2] = false; }
    armArrivals[l] = 0;
    armDepartures[l] = 0;
  }

  const maxLoopMetres = junctionLoopThreshold(gates);
  for (const feature of routes) {
    const trips = feature.properties?.trips || 0;
    if (!trips) continue;
    const coords = feature.geometry?.coordinates;
    if (!coords || coords.length < 2) continue;

    const movements = resolveRouteMovements(findGateCrossings(coords, gates), coords, maxLoopMetres);
    if (movements.length === 0) continue;

    const direction = feature.properties?.direction || 'departures';
    for (const { fromLetter, toLetter, flagged } of movements) {
      matrix[fromLetter][toLetter] += trips;
      if (flagged) flags[fromLetter][toLetter] = true;

      if (direction === 'arrivals') {
        armArrivals[fromLetter] += trips;
        armDepartures[toLetter] += trips;
      } else {
        armDepartures[fromLetter] += trips;
        armArrivals[toLetter] += trips;
      }
    }
  }

  const hasRouteData = letters.some(a => letters.some(b => a !== b && matrix[a][b] > 0));
  return { matrix, flags, armArrivals, armDepartures, hasRouteData };
}

// Draw signal lights (small R/A/G circles) inside a junction node
function SignalDots({ cx, cy, size = 5 }) {
  return (
    <g>
      <circle cx={cx} cy={cy - size * 1.6} r={size * 0.7} fill="#DC2626" />
      <circle cx={cx} cy={cy} r={size * 0.7} fill="#F59E0B" />
      <circle cx={cx} cy={cy + size * 1.6} r={size * 0.7} fill="#16A34A" />
    </g>
  );
}

export default function SchematicEditor({
  assignedNetwork, onClose,
  nodes, setNodes,
  links, setLinks,
  siteBox, setSiteBox,
  amTotal, pmTotal, modeShare,
  onEnterJunctionMapMode, matrixExportRef, onMatricesComputed
}) {
  const [mode, setMode] = useState('select');
  const [linkStart, setLinkStart] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [draggingNode, setDraggingNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [draggingBox, setDraggingBox] = useState(false);
  const [boxDragOffset, setBoxDragOffset] = useState({ x: 0, y: 0 });
  const [resizingBox, setResizingBox] = useState(false);
  const [siteNodeId, setSiteNodeId] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  // { nodeId, linkId } of the gate-letter badge currently showing its reassignment picker
  const [editingLinkGate, setEditingLinkGate] = useState(null);
  // Scenario tab: 'development' (auto from assignment) | 'baseline' | 'opening' | future year ids
  const [activeScenario, setActiveScenario] = useState('development');
  const [showMatrices, setShowMatrices] = useState(false);
  const [selectedMatrixNode, setSelectedMatrixNode] = useState(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef(null);
  const svgRef = useRef(null);

  const carPct = modeShare?.modes?.find(m => m.code === 7)?.percentage || 0;
  const amVehicle = amTotal ? Math.round(carPct / 100 * amTotal) : null;
  const pmVehicle = pmTotal ? Math.round(carPct / 100 * pmTotal) : null;

  function getSVGPoint(e) {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom
    };
  }

  function allNodes() {
    const extras = [];
    if (siteBox) extras.push(siteBoxVirtualNode(siteBox));
    return [...nodes, ...extras];
  }

  function findNearestLink(pt, threshold = 12) {
    for (const link of links) {
      const src = allNodes().find(n => n.id === link.source);
      const tgt = allNodes().find(n => n.id === link.target);
      if (!src || !tgt) continue;
      const segs = getLinkSegments(src, tgt);
      for (const seg of segs) {
        const result = pointToLineDistance(pt.x, pt.y, seg.x1, seg.y1, seg.x2, seg.y2);
        if (result.dist < threshold) return { link, nearX: result.nearX, nearY: result.nearY };
      }
    }
    return null;
  }

  function findNearestNode(pt, threshold = NODE_RADIUS + 10) {
    for (const n of nodes) {
      const dx = n.x - pt.x, dy = n.y - pt.y;
      if (Math.sqrt(dx * dx + dy * dy) < threshold) return n;
    }
    if (siteBox) {
      const vn = siteBoxVirtualNode(siteBox);
      const dx = vn.x - pt.x, dy = vn.y - pt.y;
      if (Math.sqrt(dx * dx + dy * dy) < threshold + 20) return vn;
    }
    return null;
  }

  function handleWheel(e) {
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.min(Math.max(zoom * delta, 0.2), 4);
    // Zoom towards mouse position
    setPan(prev => ({
      x: mouseX - (mouseX - prev.x) * (newZoom / zoom),
      y: mouseY - (mouseY - prev.y) * (newZoom / zoom),
    }));
    setZoom(newZoom);
  }

  function handleSVGMouseDown(e) {
    // Middle mouse or space+drag = pan; also right-click pan
    if (e.button === 1 || e.button === 2 || (e.button === 0 && mode === 'pan')) {
      e.preventDefault();
      setIsPanning(true);
      panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    }
  }

  function handleSVGMouseMove(e) {
    if (isPanning && panStart.current) {
      setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
    } else {
      handleMouseMove(e);
    }
  }

  function handleSVGMouseUpPan(e) {
    if (isPanning) {
      setIsPanning(false);
      panStart.current = null;
    } else {
      handleMouseUp(e);
    }
  }

  function handleSVGClick(e) {
    if (isPanning) return;
    if (draggingNode || draggingBox || resizingBox) return;
    if (e.target !== svgRef.current && e.target.tagName === 'rect' && e.target.getAttribute('data-box')) return;
    const pt = getSVGPoint(e);

    if (mode === 'node') {
      const nearLink = findNearestLink(pt);
      if (nearLink) {
        const snappedX = snap(nearLink.nearX);
        const snappedY = snap(nearLink.nearY);
        const newNodeId = `node-${Date.now()}`;
        const newNode = { id: newNodeId, x: snappedX, y: snappedY, label: 'Access', isSite: true, junctionType: 'priority' };
        const link1Id = `link-${Date.now()}-a`;
        const link2Id = `link-${Date.now()}-b`;
        const orig = nearLink.link;
        setNodes(prev => [...prev, newNode]);
        setLinks(prev => [
          ...prev.filter(l => l.id !== orig.id),
          { id: link1Id, source: orig.source, target: newNodeId, roadName: orig.roadName, arrivals: orig.arrivals, departures: orig.departures, pmArrivals: orig.pmArrivals, pmDepartures: orig.pmDepartures, highway: orig.highway },
          { id: link2Id, source: newNodeId, target: orig.target, roadName: orig.roadName, arrivals: orig.arrivals, departures: orig.departures, pmArrivals: orig.pmArrivals, pmDepartures: orig.pmDepartures, highway: orig.highway }
        ]);
        setSiteNodeId(newNodeId);
        return;
      }
      const id = `node-${Date.now()}`;
      setNodes(prev => [...prev, { id, x: snap(pt.x), y: snap(pt.y), label: '', isSite: false, junctionType: 'priority' }]);
      return;
    }

    if (mode === 'link') {
      const clicked = findNearestNode(pt);
      if (!linkStart) {
        // A link must START from an existing node (junction/site/terminus) —
        // clicking empty space with nothing selected does nothing.
        if (clicked) setLinkStart(clicked.id);
        return;
      }
      if (clicked) {
        if (linkStart !== clicked.id) {
          const id = `link-${Date.now()}`;
          setLinks(prev => [...prev, { id, source: linkStart, target: clicked.id, roadName: null, arrivals: null, departures: null, pmArrivals: null, pmDepartures: null, highway: null, invertFlows: false }]);
        }
        setLinkStart(null);
        return;
      }
      // Destination click landed on empty space: drop a "terminus" node here
      // (a dead-end / mid-network endpoint, not a junction) and link to it,
      // so a link no longer has to finish at a junction.
      const termId = `node-${Date.now()}`;
      setNodes(prev => [...prev, { id: termId, x: snap(pt.x), y: snap(pt.y), label: '', isSite: false, isTerminus: true }]);
      const linkId = `link-${Date.now()}`;
      setLinks(prev => [...prev, { id: linkId, source: linkStart, target: termId, roadName: null, arrivals: null, departures: null, pmArrivals: null, pmDepartures: null, highway: null, invertFlows: false }]);
      setLinkStart(null);
      return;
    }

    if (mode === 'select') {
      setSelectedNode(null);
      setSelectedLink(null);
    }
  }

  function handleLinkClick(e, linkId) {
    e.stopPropagation();
    if (mode === 'select') { setSelectedLink(linkId); setSelectedNode(null); setEditingLink(linkId); }
  }

  function handleNodeClick(e, nodeId) {
    e.stopPropagation();
    if (mode === 'link') {
      if (!linkStart) {
        setLinkStart(nodeId);
      } else {
        if (linkStart !== nodeId) {
          const id = `link-${Date.now()}`;
          setLinks(prev => [...prev, { id, source: linkStart, target: nodeId, roadName: null, arrivals: null, departures: null, pmArrivals: null, pmDepartures: null, highway: null }]);
        }
        setLinkStart(null);
      }
      return;
    }
    if (mode === 'select') { setSelectedNode(nodeId); setSelectedLink(null); setEditingLink(null); }
    if (mode === 'junction-map') {
      // A terminus is a dead-end endpoint, not a junction — nothing to map.
      if (nodes.find(n => n.id === nodeId)?.isTerminus) return;
      const numArms = links.filter(l => l.source === nodeId || l.target === nodeId).length;
      if (onEnterJunctionMapMode) onEnterJunctionMapMode(nodeId, numArms, ({ gates }) => {
        if (!gates || gates.length === 0) return;

        const centroid = gatesCentroid(gates); // [lng, lat]
        setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, mapLat: centroid[1], mapLng: centroid[0], gates } : n));

        // Match schematic links to gates so getMatrix() can label arms by
        // letter. Link flow box numbers aren't set here directly — the
        // live-sync effect (getLinkFlow, below) derives them automatically
        // from both ends' matrices once gates exist.
        const connectedLinks = links.filter(l => l.source === nodeId || l.target === nodeId);
        if (connectedLinks.length === 0) return;

        // Match schematic links to gates by comparing compass bearings: each
        // gate's real-world bearing (from the gate centroid) vs. each link's
        // bearing in the abstract schematic diagram, greedy nearest-match —
        // same mechanism the old circle-cordon system used, just fed gate
        // midpoint bearings instead of circle sector-centre bearings.
        const junctionNode = nodes.find(nd => nd.id === nodeId);
        const linkBearings = connectedLinks.map(l => {
          const otherNodeId = l.source === nodeId ? l.target : l.source;
          const otherNode = nodes.find(nd => nd.id === otherNodeId);
          if (!otherNode || !junctionNode) return { linkId: l.id, bearing: 0 };
          const dx = otherNode.x - junctionNode.x;
          const dy = otherNode.y - junctionNode.y;
          const bearing = (Math.atan2(dx, -dy) * 180 / Math.PI + 360) % 360;
          return { linkId: l.id, bearing };
        });

        // Measure each gate's bearing from the centroid of the OTHER gates,
        // not the centroid of all gates. When gates are drawn tucked in close
        // to the junction centre, every midpoint sits almost on the shared
        // all-gates centroid, so the centroid->midpoint vectors are tiny and
        // unstable — a gate straddling the centroid can flip ~180 degrees —
        // and the greedy match pairs links with the wrong gates. Excluding the
        // gate itself from its own reference point keeps each bearing well
        // separated regardless of how close to centre the gates were drawn.
        // (Falls back to the all-gates centroid when there's only one gate.)
        const gateBearings = gates.map(g => {
          const others = gates.filter(o => o.id !== g.id);
          const ref = others.length ? gatesCentroid(others) : centroid;
          return { gateId: g.id, bearing: gateGeoBearing(ref, gateMidpoint(g)) };
        });

        const linkGatePairs = [];
        for (const lb of linkBearings) {
          for (const gb of gateBearings) {
            const diff = Math.abs(((lb.bearing - gb.bearing + 540) % 360) - 180);
            linkGatePairs.push({ linkId: lb.linkId, gateId: gb.gateId, diff });
          }
        }
        linkGatePairs.sort((a, b) => a.diff - b.diff);

        const linkGateMap = {};
        const assignedLinks = new Set();
        const assignedGates = new Set();
        for (const pair of linkGatePairs) {
          if (assignedLinks.has(pair.linkId) || assignedGates.has(pair.gateId)) continue;
          linkGateMap[pair.linkId] = pair.gateId;
          assignedLinks.add(pair.linkId);
          assignedGates.add(pair.gateId);
        }

        // Store the linkId -> gateId map on the node for later use by getMatrix
        setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, linkGateMap } : n));

        // Pre-fill each matched link's road name from its gate's auto-detected
        // name (set when the gate was drawn), without overwriting a name the
        // user has already entered.
        setLinks(prev => prev.map(l => {
          const gid = linkGateMap[l.id];
          if (!gid || l.roadName) return l;
          const g = gates.find(x => x.id === gid);
          return (g && g.roadName) ? { ...l, roadName: g.roadName } : l;
        }));
      });
    }
  }

  function handleSiteBoxClick(e) {
    e.stopPropagation();
    if (mode === 'link') {
      const vn = siteBoxVirtualNode(siteBox);
      if (!linkStart) {
        setLinkStart(vn.id);
      } else {
        if (linkStart !== vn.id) {
          const id = `link-${Date.now()}`;
          setLinks(prev => [...prev, { id, source: linkStart, target: vn.id, roadName: null, arrivals: null, departures: null, pmArrivals: null, pmDepartures: null, highway: null }]);
        }
        setLinkStart(null);
      }
    }
  }

  function handleNodeMouseDown(e, nodeId) {
    e.stopPropagation();
    if (mode !== 'node' && mode !== 'select') return;
    const pt = getSVGPoint(e);
    const node = nodes.find(n => n.id === nodeId);
    setDraggingNode(nodeId);
    setDragOffset({ x: pt.x - node.x, y: pt.y - node.y });
  }

  function handleBoxMouseDown(e) {
    e.stopPropagation();
    if (!siteBox) return;
    if (mode === 'link') { handleSiteBoxClick(e); return; }
    const pt = getSVGPoint(e);
    setDraggingBox(true);
    setBoxDragOffset({ x: pt.x - siteBox.x, y: pt.y - siteBox.y });
  }

  function handleResizeMouseDown(e) {
    e.stopPropagation();
    setResizingBox(true);
  }

  function handleMouseMove(e) {
    const pt = getSVGPoint(e);
    if (draggingNode) {
      setNodes(prev => prev.map(n =>
        n.id === draggingNode ? { ...n, x: snap(pt.x - dragOffset.x), y: snap(pt.y - dragOffset.y) } : n
      ));
    }
    if (draggingBox && siteBox) {
      setSiteBox(prev => ({ ...prev, x: pt.x - boxDragOffset.x, y: pt.y - boxDragOffset.y }));
    }
    if (resizingBox && siteBox) {
      setSiteBox(prev => ({ ...prev, w: Math.max(40, pt.x - prev.x), h: Math.max(30, pt.y - prev.y) }));
    }
  }

  function handleMouseUp() {
    setDraggingNode(null);
    setDraggingBox(false);
    setResizingBox(false);
  }

  function deleteNode(nodeId) {
    setNodes(prev => prev.filter(n => n.id !== nodeId));
    setLinks(prev => prev.filter(l => l.source !== nodeId && l.target !== nodeId));
    if (siteNodeId === nodeId) setSiteNodeId(null);
    if (selectedNode === nodeId) setSelectedNode(null);
  }

  function deleteLink(linkId) {
    setLinks(prev => prev.filter(l => l.id !== linkId));
    if (selectedLink === linkId) { setSelectedLink(null); setEditingLink(null); }
  }

  function markAsSite(nodeId) {
    setSiteNodeId(nodeId);
    setNodes(prev => prev.map(n => ({ ...n, isSite: n.id === nodeId })));
  }

  function addSiteBox() {
    setSiteBox({ x: 100, y: 100, w: 120, h: 80 });
  }

  function updateLinkFlows(linkId, field, value) {
    setLinks(prev => prev.map(l =>
      l.id === linkId ? { ...l, [field]: value === '' ? null : parseInt(value) || null } : l
    ));
  }

  function updateNodeLabel(nodeId, label) {
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, label } : n));
  }

  function updateNodeType(nodeId, junctionType) {
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, junctionType } : n));
  }

  function tripColor(arrivals, departures) {
    const total = (arrivals || 0) + (departures || 0);
    if (!total) return '#C4BDD4';
    const allTotals = links.map(l => (l.arrivals || 0) + (l.departures || 0));
    const max = Math.max(...allTotals, 1);
    const intensity = total / max;
    if (intensity > 0.66) return '#D00000';
    if (intensity > 0.33) return '#E85D00';
    return '#C89000';
  }

  // Link stroke width scales with total flow so the schematic reads as a
  // bandwidth plot (thicker line = more traffic). Widened the range (2–14px
  // vs the old 2–7) so busy and quiet links are clearly distinguishable at
  // a glance, and floored nonzero flows a touch above the zero-flow width so
  // even light links look "live".
  function tripWidth(arrivals, departures) {
    const total = (arrivals || 0) + (departures || 0);
    if (!total) return 2;
    const allTotals = links.map(l => (l.arrivals || 0) + (l.departures || 0));
    const max = Math.max(...allTotals, 1);
    return 3 + (total / max) * 11;
  }

  // Display name for a junction: its custom label if set, otherwise
  // "Junction N" numbered by creation order (nodes are only ever appended,
  // so array position === creation order) among non-site nodes — not
  // derived from the id's timestamp suffix, which is meaningless to a user.
  function getJunctionLabel(node) {
    if (!node) return 'Junction';
    if (node.label) return node.label;
    const junctionNodes = nodes.filter(n => !n.isSite && !n.isTerminus);
    const idx = junctionNodes.findIndex(n => n.id === node.id);
    return idx >= 0 ? `Junction ${idx + 1}` : 'Junction';
  }

  // Build the full arm x arm turning matrix for one junction node, from its
  // connected links and (if mapped) its gates. Used by the matrix panel, CSV
  // export, and the live link-flow sync below.
  function getMatrix(nodeId) {
    const connectedLinks = links.filter(l => l.source === nodeId || l.target === nodeId);
    if (connectedLinks.length === 0) return null;

    const junctionNode = nodes.find(n => n.id === nodeId);
    const gates = junctionNode?.gates;
    const hasMapCoords = !!(gates && gates.length > 0);
    const routes = assignedNetwork?.routes?.features || [];
    const storedLinkGateMap = junctionNode?.linkGateMap;

    // Build arms from connected links — one per link, named after its
    // matched gate letter (falls back to positional "Arm N" until mapped)
    const arms = connectedLinks.map((l, i) => {
      const otherNodeId = l.source === nodeId ? l.target : l.source;
      const otherNode = nodes.find(n => n.id === otherNodeId);
      let schemBearing = i * (360 / connectedLinks.length); // fallback
      if (otherNode) {
        const dx = otherNode.x - junctionNode.x;
        const dy = otherNode.y - junctionNode.y;
        schemBearing = (Math.atan2(dx, -dy) * 180 / Math.PI + 360) % 360;
      }
      const gateId = storedLinkGateMap?.[l.id];
      const gate = gateId !== undefined ? gates?.find(g => g.id === gateId) : null;

      return {
        linkId: l.id,
        name: gate ? gate.letter : `Arm ${i + 1}`,
        gateId: gate ? gate.id : null,
        schemBearing,
        arrivals: l.arrivals || 0,
        departures: l.departures || 0,
        pmArrivals: l.pmArrivals || 0,
        pmDepartures: l.pmDepartures || 0,
      };
    });

    const totalArr = arms.reduce((s, a) => s + a.arrivals, 0);
    const totalDep = arms.reduce((s, a) => s + a.departures, 0);

    // Initialise matrix
    const matrix = {};
    for (const fromArm of arms) {
      matrix[fromArm.name] = {};
      for (const toArm of arms) {
        matrix[fromArm.name][toArm.name] = 0;
      }
    }

    if (hasMapCoords && routes.length > 0) {
      const { matrix: gateMatrix, flags, armArrivals, armDepartures, hasRouteData } = computeGateCrossings(gates, routes);

      const matrixFlags = {};
      const armArrivalsByName = {};
      const armDeparturesByName = {};
      for (const fromArm of arms) {
        matrixFlags[fromArm.name] = {};
        for (const toArm of arms) {
          matrix[fromArm.name][toArm.name] = gateMatrix[fromArm.name]?.[toArm.name] || 0;
          matrixFlags[fromArm.name][toArm.name] = flags[fromArm.name]?.[toArm.name] || false;
        }
        armArrivalsByName[fromArm.name] = armArrivals[fromArm.name] || 0;
        armDeparturesByName[fromArm.name] = armDepartures[fromArm.name] || 0;
      }

      return {
        arms, matrix, matrixFlags, totalArr, totalDep, hasMapCoords,
        hasRouteData,
        armArrivals: armArrivalsByName,
        armDepartures: armDeparturesByName,
      };
    }

    // Fallback: proportional distribution
    for (const fromArm of arms) {
      const otherArms = arms.filter(a => a.linkId !== fromArm.linkId);
      const otherTotalDep = otherArms.reduce((s, a) => s + a.departures, 0);
      for (const toArm of arms) {
        if (fromArm.name === toArm.name) continue;
        const prop = otherTotalDep > 0 ? toArm.departures / otherTotalDep : 1 / otherArms.length;
        matrix[fromArm.name][toArm.name] = Math.round(fromArm.arrivals * prop);
      }
    }

    return { arms, matrix, totalArr, totalDep, hasRouteData: false, hasMapCoords };
  }

  // Arm ordering shared by the matrix table and every export: gate letters
  // (A, B, C…) alphabetical, "Arm N" fallbacks numeric.
  const sortArm = (a, b) => {
    const numA = a.startsWith('Arm ') ? parseInt(a.slice(4)) : null;
    const numB = b.startsWith('Arm ') ? parseInt(b.slice(4)) : null;
    if (numA !== null && numB !== null) return numA - numB;
    return a.localeCompare(b);
  };

  // Draw a junction diagram to a canvas and return it as a PNG data URL: each
  // arm is a line radiating from the centre at its schematic bearing (0 =
  // up/north) labelled with its letter + road name; the centre reflects the
  // junction type (priority / roundabout / signals / signalised roundabout /
  // site access). Used by the Excel and PDF exports (component scope so both
  // the matrix panel and the ref-exposed packager below can reach it).
  function drawJunctionDiagramPng(nodeId, nodeLabel) {
    const result = getMatrix(nodeId);
    if (!result) return null;
    const { arms } = result;
    const size = 340, cx = size / 2, cy = size / 2, R = 118;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, size, size);
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';

    for (const a of arms) {
      const rad = (a.schemBearing || 0) * Math.PI / 180;
      const ex = cx + Math.sin(rad) * R;
      const ey = cy - Math.cos(rad) * R;
      ctx.strokeStyle = '#7B4DBF'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(ex, ey); ctx.stroke();
      ctx.fillStyle = '#7B4DBF';
      ctx.beginPath(); ctx.arc(ex, ey, 5, 0, Math.PI * 2); ctx.fill();

      const lx = cx + Math.sin(rad) * (R + 26);
      const ly = cy - Math.cos(rad) * (R + 26);
      const road = links.find(l => l.id === a.linkId)?.roadName;
      ctx.fillStyle = '#E85D00'; ctx.font = 'bold 16px Arial';
      ctx.fillText(a.name, lx, ly - (road ? 8 : 0));
      if (road) {
        ctx.fillStyle = '#3D1A6E'; ctx.font = '11px Arial';
        ctx.fillText(road.length > 16 ? road.slice(0, 15) + '…' : road, lx, ly + 8);
      }
    }

    const node = nodes.find(n => n.id === nodeId);
    const jt = node?.junctionType || 'priority';
    const isSite = nodeId === siteNodeId;
    const half = 24;
    const signalDots = (s) => {
      ctx.fillStyle = '#DC2626'; ctx.beginPath(); ctx.arc(cx, cy - s * 1.6, s * 0.7, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#F59E0B'; ctx.beginPath(); ctx.arc(cx, cy, s * 0.7, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#16A34A'; ctx.beginPath(); ctx.arc(cx, cy + s * 1.6, s * 0.7, 0, Math.PI * 2); ctx.fill();
    };
    ctx.lineWidth = 3; ctx.strokeStyle = '#7B6A96';

    if (isSite) {
      ctx.fillStyle = '#3D1A6E'; ctx.fillRect(cx - 32, cy - 22, 64, 44);
      ctx.fillStyle = '#ffffff'; ctx.font = 'bold 11px Arial';
      ctx.fillText('SITE', cx, cy - 5); ctx.fillText('ACCESS', cx, cy + 8);
    } else if (jt === 'roundabout') {
      ctx.fillStyle = '#ffffff'; ctx.beginPath(); ctx.arc(cx, cy, half, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.fillStyle = 'rgba(123,106,150,0.3)'; ctx.beginPath(); ctx.arc(cx, cy, half * 0.45, 0, Math.PI * 2); ctx.fill();
    } else if (jt === 'signals') {
      ctx.fillStyle = '#ffffff'; ctx.fillRect(cx - half, cy - half, half * 2, half * 2); ctx.strokeRect(cx - half, cy - half, half * 2, half * 2);
      signalDots(5);
    } else if (jt === 'signals_roundabout') {
      ctx.fillStyle = '#ffffff'; ctx.beginPath(); ctx.arc(cx, cy, half, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.fillStyle = 'rgba(123,106,150,0.15)'; ctx.beginPath(); ctx.arc(cx, cy, half * 0.45, 0, Math.PI * 2); ctx.fill();
      signalDots(4);
    } else {
      ctx.fillStyle = '#ffffff'; ctx.fillRect(cx - half, cy - half, half * 2, half * 2); ctx.strokeRect(cx - half, cy - half, half * 2, half * 2);
    }

    if (!isSite) {
      const name = (nodeLabel || '').slice(0, 18);
      ctx.font = 'bold 12px Arial';
      const w = ctx.measureText(name).width;
      ctx.fillStyle = '#ffffff'; ctx.fillRect(cx - w / 2 - 4, cy + half + 8, w + 8, 16);
      ctx.fillStyle = '#3D1A6E'; ctx.fillText(name, cx, cy + half + 16);
    }
    return canvas.toDataURL('image/png');
  }

  // Package every junction's diagram + matrix as plain data for the PDF
  // export (which lives in a separate module). Exposed to App.js through the
  // matrixExportRef so the PDF can render junction turning matrices.
  function getJunctionMatrixExport() {
    const out = [];
    for (const n of nodes) {
      if (n.isTerminus) continue;
      if (!links.some(l => l.source === n.id || l.target === n.id)) continue;
      const result = getMatrix(n.id);
      if (!result) continue;
      const label = getJunctionLabel(n);
      const armNames = result.arms.map(a => a.name).sort(sortArm);
      out.push({ label, armNames, matrix: result.matrix, diagramPng: drawJunctionDiagramPng(n.id, label) });
    }
    return out;
  }

  useEffect(() => {
    if (matrixExportRef) matrixExportRef.current = getJunctionMatrixExport;
  });

  // Push the computed turning matrices up to App state (for the Results
  // junction view + its Excel export) whenever the schematic changes. Includes
  // the per-junction diagram PNG so the Results export can match the schematic
  // one. Runs only on nodes/links change, so it can't loop on parent renders.
  useEffect(() => {
    if (!onMatricesComputed) return;
    try {
      const out = [];
      for (const n of nodes) {
        if (n.isTerminus) continue;
        if (!links.some(l => l.source === n.id || l.target === n.id)) continue;
        const result = getMatrix(n.id);
        if (!result) continue;
        const label = getJunctionLabel(n);
        out.push({ label, armNames: result.arms.map(a => a.name).sort(sortArm), matrix: result.matrix, diagramPng: drawJunctionDiagramPng(n.id, label) });
      }
      onMatricesComputed(out);
    } catch (e) { /* incomplete schematic — leave matrices unchanged */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, links]);

  // A link between two junctions is bounded by one gate at each end. That
  // gate's column total in its junction's matrix (movements ENDING there)
  // is traffic leaving that junction toward the other end; its row total
  // (movements STARTING there) is traffic arriving from the other end —
  // physically real directional flow, not a site-relative approximation.
  // If both ends are mapped, each direction has two independent estimates
  // (source's column ~= target's row, and vice versa) — average them. If
  // only one end is mapped, its row/column pair already covers both
  // directions on that arm. Neither end mapped (e.g. a link to the site
  // node) -> null, falls back to manual entry.
  function getLinkFlow(link) {
    const srcNode = nodes.find(n => n.id === link.source);
    const tgtNode = nodes.find(n => n.id === link.target);

    function armTotals(nodeId) {
      if (!nodeId) return null;
      const node = nodes.find(n => n.id === nodeId);
      if (!node?.gates?.length) return null;
      const result = getMatrix(nodeId);
      const arm = result?.arms.find(a => a.linkId === link.id);
      if (!arm) return null;
      const outbound = result.arms.reduce((s, a) => s + (result.matrix[a.name]?.[arm.name] || 0), 0);
      const inbound = result.arms.reduce((s, a) => s + (result.matrix[arm.name]?.[a.name] || 0), 0);
      return { outbound, inbound }; // outbound = toward the other end, inbound = from the other end
    }

    const srcTotals = armTotals(srcNode?.id);
    const tgtTotals = armTotals(tgtNode?.id);
    if (!srcTotals && !tgtTotals) return null;

    const towardTarget = srcTotals && tgtTotals ? (srcTotals.outbound + tgtTotals.inbound) / 2
      : srcTotals ? srcTotals.outbound : tgtTotals.inbound;
    const towardSource = srcTotals && tgtTotals ? (srcTotals.inbound + tgtTotals.outbound) / 2
      : srcTotals ? srcTotals.inbound : tgtTotals.outbound;

    return { towardTarget: Math.round(towardTarget), towardSource: Math.round(towardSource) };
  }

  // Which gate letter (if any) a link is currently matched to at one of its
  // end junctions. The match is auto-computed once (schematic bearing vs.
  // real compass bearing, greedy nearest) when that junction's gates are
  // confirmed — since schematic diagrams aren't drawn to true compass
  // orientation, that guess can pair a link with the wrong gate and silently
  // feed getLinkFlow the wrong arm's numbers. Shown as an on-canvas badge so
  // the pairing is visible and fixable instead of just trusted.
  function getLinkGateLetter(link, nodeId) {
    const node = nodes.find(n => n.id === nodeId);
    const gateId = node?.linkGateMap?.[link.id];
    if (gateId === undefined || gateId === null) return null;
    return node.gates?.find(g => g.id === gateId)?.letter || null;
  }

  // Manually reassign which gate a link is matched to at a junction,
  // overriding the auto bearing-match. Keeps the mapping one-to-one: if
  // another link at this junction already held the chosen gate, it's
  // unmapped first so two links can't silently share/double-count one gate.
  function reassignLinkGate(nodeId, linkId, newGateId) {
    setNodes(prev => prev.map(n => {
      if (n.id !== nodeId) return n;
      const map = { ...(n.linkGateMap || {}) };
      for (const lid of Object.keys(map)) {
        if (map[lid] === newGateId) delete map[lid];
      }
      if (newGateId) map[linkId] = newGateId; else delete map[linkId];
      return { ...n, linkGateMap: map };
    }));
  }

  // Keep link arrivals/departures in sync with the junction matrices
  // automatically — no manual "map to road" or "apply" step. Only touches
  // links where at least one end is gate-mapped; links with neither end
  // mapped (e.g. to the site node) are left alone for manual entry.
  // links.length (not links itself) is in the dependency array so a newly
  // added/removed link gets picked up without re-running every time this
  // effect's own setLinks call replaces the array.
  useEffect(() => {
    setLinks(prev => {
      let changed = false;
      const next = prev.map(l => {
        const flow = getLinkFlow(l);
        if (!flow) return l;
        if (l.arrivals === flow.towardTarget && l.departures === flow.towardSource) return l;
        changed = true;
        return { ...l, arrivals: flow.towardTarget, departures: flow.towardSource };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, assignedNetwork, links.length]);

  // Get values to display based on active scenario
  function getLinkDisplayValues(link) {
    if (activeScenario === 'development') {
      return {
        amArr: link.arrivals,
        amDep: link.departures,
        pmArr: link.pmArrivals,
        pmDep: link.pmDepartures,
      };
    }
    if (activeScenario === 'baseline') {
      return {
        amArr: link.baselineAmArrivals,
        amDep: link.baselineAmDepartures,
        pmArr: link.baselinePmArrivals,
        pmDep: link.baselinePmDepartures,
      };
    }
    if (activeScenario === 'opening') {
      // Opening year = baseline + development
      return {
        amArr: (link.baselineAmArrivals || 0) + (link.arrivals || 0) || null,
        amDep: (link.baselineAmDepartures || 0) + (link.departures || 0) || null,
        pmArr: (link.baselinePmArrivals || 0) + (link.pmArrivals || 0) || null,
        pmDep: (link.baselinePmDepartures || 0) + (link.pmDepartures || 0) || null,
      };
    }
    return { amArr: null, amDep: null, pmArr: null, pmDep: null };
  }

  function getCompassLabel(bearing) {
    const dirs = ['N','NE','E','SE','S','SW','W','NW'];
    return dirs[Math.round(bearing / 45) % 8] + 'B';
  }

  function getLinkDirectionLabels(link) {
    // Always anchor on the link's own source->target direction (schematic
    // coordinates) — NOT on whichever end happens to be gate-mapped. This
    // used to prefer the mapped end (source if source.mapLat, else target),
    // which silently flipped dir1/dir2 the moment the *other* end also got
    // gate-mapped and source became preferred (source is always preferred
    // once both are mapped) — same underlying numbers, reversed compass
    // labels. Anchoring on source unconditionally is stable regardless of
    // gate-mapping order, and matches what this already did whenever neither
    // or both ends were mapped — only the target-only-mapped case differed.
    const srcNode = nodes.find(n => n.id === link.source);
    const tgtNode = nodes.find(n => n.id === link.target);
    const dx = (tgtNode?.x || 0) - (srcNode?.x || 0);
    const dy = (tgtNode?.y || 0) - (srcNode?.y || 0);
    const bearing = (Math.atan2(dx, -dy) * 180 / Math.PI + 360) % 360;
    const oppBearing = (bearing + 180) % 360;
    return {
      dir1: getCompassLabel(bearing),
      dir2: getCompassLabel(oppBearing),
    };
  }

  function renderFlowBoxes(link, src, tgt, color, isSelected) {
    const hLen = Math.abs(tgt.x - src.x);
    const vLen = Math.abs(tgt.y - src.y);
    const useHoriz = hLen >= vLen;

    let mx, my, ux, uy;
    if (useHoriz) {
      mx = (src.x + tgt.x) / 2;
      my = src.y;
      ux = tgt.x > src.x ? 1 : -1;
      uy = 0;
    } else {
      mx = tgt.x;
      my = (src.y + tgt.y) / 2;
      ux = 0;
      uy = tgt.y > src.y ? 1 : -1;
    }

    const px = -uy, py = ux;
    const BW = 76, BH = 22, CW = 36;
    // The boxes sit perpendicular to the link. On a horizontal link they're
    // offset vertically and only the box HEIGHT (22) crosses the link, so a
    // small offset clears it. On a vertical link they're offset horizontally
    // and the box WIDTH (76) crosses the link, so the offset must exceed half
    // the width to keep the two boxes clear of the link (and each other).
    const OFFSET = useHoriz ? 32 : BW / 2 + 16;

    const ax = mx + px * OFFSET;
    const ay = my + py * OFFSET;
    const depx = mx - px * OFFSET;
    const depy = my - py * OFFSET;

    const AS = 7;
    const arrArrow = `${ax + ux * AS},${ay + uy * AS} ${ax - ux * AS + py * AS},${ay - uy * AS - px * AS} ${ax - ux * AS - py * AS},${ay - uy * AS + px * AS}`;
    const depArrow = `${depx - ux * AS},${depy - uy * AS} ${depx + ux * AS + py * AS},${depy + uy * AS - px * AS} ${depx + ux * AS - py * AS},${depy + uy * AS + px * AS}`;

    // UK left-hand traffic: each direction of travel keeps to the LEFT of
    // its own heading, so on the schematic the toward-target flow belongs on
    // the FAR box and toward-source on the NEAR box. This isn't a preference
    // — the far box (px/py above) is geometrically the left-hand side of the
    // src->target heading by construction, so this rule is fixed and
    // independent of which end was drawn first or which junctions are gate-
    // mapped. (The old code anchored on whichever end happened to be mapped,
    // which is why mapping the SECOND junction silently flipped every box to
    // the wrong side — same numbers, wrong carriageway.) The per-link "Swap
    // flow sides" button still overrides this for genuine exceptions such as
    // one-way slips. inv=false => near shows toward-source, far shows
    // toward-target.
    const inv = !!link.invertFlows;
    const vals = getLinkDisplayValues(link);
    const amArrVal = inv ? vals.amArr : vals.amDep;
    const amDepVal = inv ? vals.amDep : vals.amArr;
    const pmArrVal = inv ? vals.pmArr : vals.pmDep;
    const pmDepVal = inv ? vals.pmDep : vals.pmArr;

    const hasAmArr = amArrVal !== null && amArrVal > 0;
    const hasAmDep = amDepVal !== null && amDepVal > 0;
    const hasPmArr = pmArrVal !== null && pmArrVal > 0;
    const hasPmDep = pmDepVal !== null && pmDepVal > 0;
    const hasAnyArr = hasAmArr || hasPmArr;
    const hasAnyDep = hasAmDep || hasPmDep;

    // dir1/dir2 are the compass labels for source->target / target->source.
    // They must swap sides in lockstep with amArrVal/amDepVal above (same
    // `inv` check) — otherwise "Swap arrival/departure sides" moves the
    // number to the other box but leaves its compass letter behind, so the
    // box ends up showing the right number under the wrong direction.
    const rawDirs = getLinkDirectionLabels(link);
    const dir1 = inv ? rawDirs.dir1 : rawDirs.dir2;
    const dir2 = inv ? rawDirs.dir2 : rawDirs.dir1;

    // Road name: horizontal links get it above the midpoint; vertical links
    // get it rotated to run ALONG the link, offset clear of the boxes on the
    // left, rather than sitting horizontally on top of the link line.
    const roadX = useHoriz ? mx : mx - (OFFSET + BW / 2 + 12);
    const roadY = useHoriz ? my - OFFSET - 18 : my;

    return (
      <g>
        {link.roadName && (
          <text x={roadX} y={roadY} textAnchor="middle"
            transform={useHoriz ? undefined : `rotate(-90 ${roadX} ${roadY})`}
            style={{ fontSize: 9, fill: '#6B6880', fontFamily: 'Arial', pointerEvents: 'none', textTransform: 'uppercase' }}>
            {link.roadName}
          </text>
        )}

        {/* Direction 1 box (near side) */}
        <g onClick={e => { e.stopPropagation(); setEditingLink(link.id); setSelectedLink(link.id); setMode('select'); }} style={{ cursor: 'pointer' }}>
          <rect x={ax - BW / 2} y={ay - BH / 2} width={BW} height={BH} rx={4}
            fill={hasAnyArr ? '#EDE5F8' : '#F9F7FD'}
            stroke={hasAnyArr ? '#7B4DBF' : '#C4BDD4'}
            strokeWidth={hasAnyArr ? 1.5 : 1}
            strokeDasharray={hasAnyArr ? 'none' : '3,2'} />
          <line x1={ax} y1={ay - BH / 2} x2={ax} y2={ay + BH / 2} stroke={hasAnyArr ? '#C4BDD4' : '#E5E7EB'} strokeWidth={0.8} />
          <text x={ax - CW / 2} y={ay - 5} textAnchor="middle"
            style={{ fontSize: 7, fill: '#9B86C4', fontFamily: 'Arial', pointerEvents: 'none' }}>{dir1}</text>
          <text x={ax - CW / 2} y={ay + 6} textAnchor="middle"
            style={{ fontSize: 10, fontWeight: 700, fill: hasAmArr ? '#3D1A6E' : '#C4BDD4', fontFamily: 'Arial', pointerEvents: 'none' }}>
            {hasAmArr ? amArrVal : '—'}
          </text>
          <text x={ax + CW / 2} y={ay - 5} textAnchor="middle"
            style={{ fontSize: 7, fill: '#0D9488', fontFamily: 'Arial', pointerEvents: 'none' }}>PM</text>
          <text x={ax + CW / 2} y={ay + 6} textAnchor="middle"
            style={{ fontSize: 10, fontWeight: 700, fill: hasPmArr ? '#0D9488' : '#C4BDD4', fontFamily: 'Arial', pointerEvents: 'none' }}>
            {hasPmArr ? pmArrVal : '—'}
          </text>
          <polygon points={arrArrow} fill={hasAnyArr ? '#7B4DBF' : '#C4BDD4'} />
        </g>

        {/* Direction 2 box (far side) */}
        <g onClick={e => { e.stopPropagation(); setEditingLink(link.id); setSelectedLink(link.id); setMode('select'); }} style={{ cursor: 'pointer' }}>
          <rect x={depx - BW / 2} y={depy - BH / 2} width={BW} height={BH} rx={4}
            fill={hasAnyDep ? '#F0FDF9' : '#F9F7FD'}
            stroke={hasAnyDep ? '#0D9488' : '#C4BDD4'}
            strokeWidth={hasAnyDep ? 1.5 : 1}
            strokeDasharray={hasAnyDep ? 'none' : '3,2'} />
          <line x1={depx} y1={depy - BH / 2} x2={depx} y2={depy + BH / 2} stroke={hasAnyDep ? '#C4BDD4' : '#E5E7EB'} strokeWidth={0.8} />
          <text x={depx - CW / 2} y={depy - 5} textAnchor="middle"
            style={{ fontSize: 7, fill: '#9B86C4', fontFamily: 'Arial', pointerEvents: 'none' }}>{dir2}</text>
          <text x={depx - CW / 2} y={depy + 6} textAnchor="middle"
            style={{ fontSize: 10, fontWeight: 700, fill: hasAmDep ? '#0D9488' : '#C4BDD4', fontFamily: 'Arial', pointerEvents: 'none' }}>
            {hasAmDep ? amDepVal : '—'}
          </text>
          <text x={depx + CW / 2} y={depy - 5} textAnchor="middle"
            style={{ fontSize: 7, fill: '#0D9488', fontFamily: 'Arial', pointerEvents: 'none' }}>PM</text>
          <text x={depx + CW / 2} y={depy + 6} textAnchor="middle"
            style={{ fontSize: 10, fontWeight: 700, fill: hasPmDep ? '#0D9488' : '#C4BDD4', fontFamily: 'Arial', pointerEvents: 'none' }}>
            {hasPmDep ? pmDepVal : '—'}
          </text>
          <polygon points={depArrow} fill={hasAnyDep ? '#0D9488' : '#C4BDD4'} />
        </g>
      </g>
    );
  }

  // Badge at one end of a link showing which gate letter it's matched to at
  // that junction ('?' if the junction has gates but this link didn't get
  // matched to one). Click to open a picker and correct it — see
  // getLinkGateLetter/reassignLinkGate above for why this needs to be
  // user-fixable rather than just trusted. The picker itself is NOT rendered
  // here — badges live inside a link's own <g>, which draws before the Nodes
  // group and before other links later in the array, so a nested popup could
  // end up hidden behind a neighbouring node's "N gates" label, another
  // link's flow boxes, etc. Instead this just records where to draw it;
  // renderLinkGatePicker (called once, last, after Nodes) draws on top of
  // everything.
  function renderLinkGateBadge(link, node, bx, by) {
    if (!node?.gates?.length) return null;
    const letter = getLinkGateLetter(link, node.id);
    const isEditing = editingLinkGate?.linkId === link.id && editingLinkGate?.nodeId === node.id;

    return (
      <g key={`${link.id}-${node.id}-gatebadge`}
        onClick={e => { e.stopPropagation(); setEditingLinkGate(isEditing ? null : { nodeId: node.id, linkId: link.id, x: bx, y: by }); }}
        style={{ cursor: 'pointer' }}>
        <circle cx={bx} cy={by} r={8} fill={letter ? '#FEF3E8' : '#FEE2E2'} stroke={letter ? '#E85D00' : '#E8001C'} strokeWidth={1.5} />
        <text x={bx} y={by + 3} textAnchor="middle"
          style={{ fontSize: 9, fontWeight: 700, fill: letter ? '#E85D00' : '#E8001C', fontFamily: 'Arial', pointerEvents: 'none' }}>
          {letter || '?'}
        </text>
      </g>
    );
  }

  // The gate-reassignment picker for whichever badge is currently active
  // (editingLinkGate). Rendered once, as the very last thing in the canvas's
  // transformed <g>, so it always draws on top of every link/node/flow-box —
  // see renderLinkGateBadge above for why. A transparent full-canvas rect
  // behind the picker closes it on any outside click (its own options
  // stopPropagation so they don't trigger this).
  function renderLinkGatePicker() {
    if (!editingLinkGate) return null;
    const { nodeId, linkId, x, y } = editingLinkGate;
    const node = nodes.find(n => n.id === nodeId);
    if (!node?.gates?.length) return null;
    const currentLetter = getLinkGateLetter({ id: linkId }, nodeId);
    const OPT = 20;

    return (
      <g>
        <rect x={-4000} y={-4000} width={12000} height={12000} fill="transparent" onClick={() => setEditingLinkGate(null)} />
        <rect x={x + 12} y={y - 10} width={OPT * (node.gates.length + 1) + 4} height={20} rx={4} fill="#1E1A2E" stroke="#3D1A6E" />
        {node.gates.map((g, i) => (
          <g key={g.id} onClick={e => { e.stopPropagation(); reassignLinkGate(nodeId, linkId, g.id); setEditingLinkGate(null); }}
            style={{ cursor: 'pointer' }}>
            <rect x={x + 14 + i * OPT} y={y - 8} width={16} height={16} rx={3} fill={currentLetter === g.letter ? '#3D1A6E' : 'transparent'} />
            <text x={x + 22 + i * OPT} y={y + 4} textAnchor="middle"
              style={{ fontSize: 9, fontWeight: 700, fill: 'white', fontFamily: 'Arial', pointerEvents: 'none' }}>{g.letter}</text>
          </g>
        ))}
        <g onClick={e => { e.stopPropagation(); reassignLinkGate(nodeId, linkId, null); setEditingLinkGate(null); }}
          style={{ cursor: 'pointer' }}>
          <rect x={x + 14 + node.gates.length * OPT} y={y - 8} width={16} height={16} rx={3} fill="transparent" />
          <text x={x + 22 + node.gates.length * OPT} y={y + 4} textAnchor="middle"
            style={{ fontSize: 9, fontWeight: 700, fill: '#E8001C', fontFamily: 'Arial', pointerEvents: 'none' }}>✕</text>
        </g>
      </g>
    );
  }

  // Render junction node shape based on type
  function renderNodeShape(node, isSelected, isLinkStart) {
    const { x, y, junctionType = 'priority', id } = node;
    const isSite = id === siteNodeId;
    const NR = NODE_RADIUS;

    if (isSite) {
      return (
        <>
          <rect x={x - 26} y={y - 18} width={52} height={36} rx={4} fill="#3D1A6E" stroke="#3D1A6E" strokeWidth={2} />
          <text x={x} y={y - 4} textAnchor="middle" style={{ fontSize: 8, fontWeight: 700, fill: 'white', fontFamily: 'Arial', pointerEvents: 'none' }}>SITE</text>
          <text x={x} y={y + 8} textAnchor="middle" style={{ fontSize: 8, fill: '#C4BDD4', fontFamily: 'Arial', pointerEvents: 'none' }}>ACCESS</text>
        </>
      );
    }

    // Terminus (dead-end / mid-network endpoint) — a small open node, not a
    // junction box. It's a real link endpoint but carries no turning matrix.
    if (node.isTerminus) {
      const tStroke = isSelected ? '#0D9488' : '#7B6A96';
      return (
        <>
          <circle cx={x} cy={y} r={7} fill="white" stroke={tStroke} strokeWidth={isSelected ? 2.5 : 2} />
          <circle cx={x} cy={y} r={2.5} fill={tStroke} />
          {node.label && (
            <text x={x} y={y - 12} textAnchor="middle" style={{ fontSize: 8, fontWeight: 700, fill: '#3D1A6E', fontFamily: 'Arial', pointerEvents: 'none' }}>
              {node.label.substring(0, 12)}
            </text>
          )}
        </>
      );
    }

    const strokeColor = isSelected ? '#0D9488' : '#7B6A96';
    const fillColor = isSelected ? '#EDE5F8' : 'white';
    const sw = isSelected ? 2.5 : 2;

    if (junctionType === 'roundabout') {
      return (
        <>
          <circle cx={x} cy={y} r={NR} fill={fillColor} stroke={strokeColor} strokeWidth={sw} />
          {/* Inner circle to indicate roundabout */}
          <circle cx={x} cy={y} r={NR * 0.45} fill={strokeColor} opacity={0.3} />
          {node.label && (
            <text x={x} y={y + 4} textAnchor="middle" style={{ fontSize: 8, fontWeight: 700, fill: '#3D1A6E', fontFamily: 'Arial', pointerEvents: 'none' }}>
              {node.label.substring(0, 4).toUpperCase()}
            </text>
          )}
        </>
      );
    }

    if (junctionType === 'signals') {
      return (
        <>
          <rect x={x - NR} y={y - NR} width={NR * 2} height={NR * 2} rx={2} fill={fillColor} stroke={strokeColor} strokeWidth={sw} />
          <SignalDots cx={x} cy={y} size={4} />
          {node.label && (
            <text x={x + NR + 4} y={y + 4} textAnchor="start" style={{ fontSize: 8, fontWeight: 700, fill: '#3D1A6E', fontFamily: 'Arial', pointerEvents: 'none' }}>
              {node.label.substring(0, 4).toUpperCase()}
            </text>
          )}
        </>
      );
    }

    if (junctionType === 'signals_roundabout') {
      return (
        <>
          <circle cx={x} cy={y} r={NR} fill={fillColor} stroke={strokeColor} strokeWidth={sw} />
          <circle cx={x} cy={y} r={NR * 0.45} fill={strokeColor} opacity={0.15} />
          <SignalDots cx={x} cy={y} size={3.5} />
          {node.label && (
            <text x={x + NR + 4} y={y + 4} textAnchor="start" style={{ fontSize: 8, fontWeight: 700, fill: '#3D1A6E', fontFamily: 'Arial', pointerEvents: 'none' }}>
              {node.label.substring(0, 4).toUpperCase()}
            </text>
          )}
        </>
      );
    }

    // Default: priority (square)
    return (
      <>
        <rect x={x - NR} y={y - NR} width={NR * 2} height={NR * 2} rx={2} fill={fillColor} stroke={strokeColor} strokeWidth={sw} />
        {node.label && (
          <text x={x} y={y + 4} textAnchor="middle" style={{ fontSize: 8, fontWeight: 700, fill: '#3D1A6E', fontFamily: 'Arial', pointerEvents: 'none' }}>
            {node.label.substring(0, 4).toUpperCase()}
          </text>
        )}
      </>
    );
  }

  const nodeMap = Object.fromEntries([...nodes, ...(siteBox ? [siteBoxVirtualNode(siteBox)] : [])].map(n => [n.id, n]));
  const editingLinkData = links.find(l => l.id === editingLink);
  const selectedNodeData = nodes.find(n => n.id === selectedNode);

  // Scenario tabs - always show Development; Baseline and Opening if any baseline data exists
  const hasBaseline = links.some(l => l.baselineAmArrivals || l.baselineAmDepartures || l.baselinePmArrivals || l.baselinePmDepartures);
  const scenarioTabs = [
    { key: 'development', label: 'Development' },
    { key: 'baseline', label: 'Baseline' },
    ...(hasBaseline ? [{ key: 'opening', label: 'Opening Year' }] : []),
  ];

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 6, padding: '8px 14px', borderBottom: '1px solid #EDE5F8', background: '#F5F0FA', alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { key: 'select', label: '✦ Select / Edit' },
          { key: 'node', label: '⬡ Add Junction' },
          { key: 'link', label: '— Add Link' },
          { key: 'junction-map', label: '🎯 Map Junction' },
          { key: 'pan', label: '✋ Pan' },
        ].map(m => (
          <button key={m.key} onClick={() => { setMode(m.key); setLinkStart(null); if (m.key !== 'select') { setSelectedLink(null); setEditingLink(null); setSelectedNode(null); } }}
            style={{ padding: '5px 10px', background: mode === m.key ? '#3D1A6E' : 'white', color: mode === m.key ? 'white' : '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {m.label}
          </button>
        ))}

        <div style={{ width: 1, height: 22, background: '#EDE5F8' }} />

        <button onClick={siteBox ? () => setSiteBox(null) : addSiteBox}
          style={{ padding: '5px 10px', background: siteBox ? '#E8001C' : 'white', color: siteBox ? 'white' : '#E8001C', border: '1.5px solid #E8001C', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          {siteBox ? '✕ Remove Site Box' : '⬜ Add Site Box'}
        </button>

        <button onClick={() => { setNodes([]); setLinks([]); setSiteBox(null); setLinkStart(null); setSelectedLink(null); setEditingLink(null); setSelectedNode(null); setSiteNodeId(null); }}
          style={{ padding: '5px 10px', background: 'white', color: '#C4BDD4', border: '1.5px solid #E5E7EB', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          Clear All
        </button>

        <div style={{ width: 1, height: 22, background: '#EDE5F8' }} />

        <button onClick={() => { setShowMatrices(prev => !prev); setSelectedMatrixNode(null); }}
          style={{ padding: '5px 10px', background: showMatrices ? '#3D1A6E' : 'white', color: showMatrices ? 'white' : '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          📊 Junction Matrices
        </button>

        <div style={{ marginLeft: 'auto', fontSize: 10, color: '#6B6880' }}>
          {mode === 'node' && 'Click to place junction · Click on a link to split · Drag to move'}
          {mode === 'link' && (linkStart ? 'Click another junction to connect, or click empty space for a dead-end' : 'Click source junction or site box')}
          {mode === 'select' && 'Click a junction or link to edit · Drag to move · ✕ to delete · S to mark as site'}
          {mode === 'pan' && 'Click and drag to pan · Scroll to zoom'}
          {mode === 'junction-map' && <span style={{ color: '#E85D00', fontWeight: 600 }}>Click a junction to pin it to a real-world location on the map</span>}
        </div>
      </div>

      {/* Scenario tab bar */}
      <div style={{ display: 'flex', gap: 0, padding: '6px 14px', borderBottom: '1px solid #EDE5F8', background: 'white', alignItems: 'center' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#6B6880', marginRight: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>View:</span>
        {scenarioTabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveScenario(tab.key)}
            style={{
              padding: '4px 12px',
              background: activeScenario === tab.key ? '#3D1A6E' : 'white',
              color: activeScenario === tab.key ? 'white' : '#6B6880',
              border: '1.5px solid',
              borderColor: activeScenario === tab.key ? '#3D1A6E' : '#EDE5F8',
              borderRadius: 5,
              fontSize: 11,
              fontWeight: activeScenario === tab.key ? 700 : 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginRight: 4,
            }}>
            {tab.label}
          </button>
        ))}
        <span style={{ fontSize: 10, color: '#C4BDD4', marginLeft: 8 }}>
          {activeScenario === 'development' && '— Auto-populated from trip assignment'}
          {activeScenario === 'baseline' && '— Enter existing survey counts in link editor'}
          {activeScenario === 'opening' && '— Baseline + Development'}
        </span>
      </div>

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* SVG Canvas */}
        <svg ref={svgRef}
          style={{ flex: 1, background: 'white', cursor: mode === 'pan' || isPanning ? 'grab' : mode === 'node' ? 'crosshair' : mode === 'link' ? 'crosshair' : 'default' }}
          onClick={handleSVGClick}
          onMouseDown={handleSVGMouseDown}
          onMouseMove={handleSVGMouseMove}
          onMouseUp={handleSVGMouseUpPan}
          onWheel={handleWheel}>

          <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
          {/* Grid */}
          {Array.from({ length: 30 }).map((_, i) => (
            <line key={`v${i}`} x1={i * GRID} y1={0} x2={i * GRID} y2={3000} stroke="#F8F5FC" strokeWidth={1} />
          ))}
          {Array.from({ length: 20 }).map((_, i) => (
            <line key={`h${i}`} x1={0} y1={i * GRID} x2={3000} y2={i * GRID} stroke="#F8F5FC" strokeWidth={1} />
          ))}

          {/* Site boundary box */}
          {siteBox && (
            <g>
              <rect
                data-box="true"
                x={siteBox.x} y={siteBox.y}
                width={siteBox.w} height={siteBox.h}
                fill="rgba(232,0,28,0.06)"
                stroke={mode === 'link' && linkStart === '__sitebox__' ? '#0D9488' : '#E8001C'}
                strokeWidth={mode === 'link' && linkStart === '__sitebox__' ? 3 : 2}
                strokeDasharray="8,4"
                rx={4}
                style={{ cursor: mode === 'link' ? 'pointer' : 'move' }}
                onMouseDown={handleBoxMouseDown}
              />
              <text x={siteBox.x + 6} y={siteBox.y + 14}
                style={{ fontSize: 9, fontWeight: 700, fill: '#E8001C', fontFamily: 'Arial', pointerEvents: 'none', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                SITE BOUNDARY
              </text>
              {mode === 'link' && (
                <circle cx={siteBox.x + siteBox.w / 2} cy={siteBox.y + siteBox.h / 2} r={8}
                  fill={linkStart === '__sitebox__' ? '#0D9488' : '#E8001C'} opacity={0.5}
                  style={{ pointerEvents: 'none' }} />
              )}
              <rect
                x={siteBox.x + siteBox.w - 8} y={siteBox.y + siteBox.h - 8}
                width={8} height={8} fill="#E8001C" rx={1}
                style={{ cursor: 'se-resize' }}
                onMouseDown={handleResizeMouseDown}
              />
            </g>
          )}

          {/* Links */}
          {links.map(link => {
            const src = nodeMap[link.source];
            const tgt = nodeMap[link.target];
            if (!src || !tgt) return null;
            const color = tripColor(link.arrivals, link.departures);
            const sw = tripWidth(link.arrivals, link.departures);
            const isSelected = selectedLink === link.id;
            const midX = tgt.x, midY = src.y;
            const pathD = `M ${src.x} ${src.y} L ${midX} ${midY} L ${tgt.x} ${tgt.y}`;

            const dx = tgt.x - midX, dy = tgt.y - midY;
            const len = Math.sqrt(dx * dx + dy * dy) || 1;
            const ux = dx / len, uy = dy / len;
            const arrowSize = 7;
            const ax2 = tgt.x - ux * 18, ay2 = tgt.y - uy * 18;
            const arrowPts = [[tgt.x, tgt.y], [ax2 - uy * arrowSize, ay2 + ux * arrowSize], [ax2 + uy * arrowSize, ay2 - ux * arrowSize]].map(p => p.join(',')).join(' ');
            const midLabelX = (src.x + midX) / 2, midLabelY = src.y;

            // Gate-letter badge positions: offset from each node along the
            // direction of the path's adjacent segment (falls back to the
            // straight src->tgt direction on the src.x===tgt.x / src.y===tgt.y
            // edge cases where that adjacent segment has zero length).
            let srcVx = midX - src.x, srcVy = midY - src.y;
            if (srcVx === 0 && srcVy === 0) { srcVx = tgt.x - src.x; srcVy = tgt.y - src.y; }
            let tgtVx = tgt.x - midX, tgtVy = tgt.y - midY;
            if (tgtVx === 0 && tgtVy === 0) { tgtVx = tgt.x - src.x; tgtVy = tgt.y - src.y; }
            const srcVLen = Math.sqrt(srcVx * srcVx + srcVy * srcVy) || 1;
            const tgtVLen = Math.sqrt(tgtVx * tgtVx + tgtVy * tgtVy) || 1;
            const BADGE_OFFSET = 28;
            const srcBadgeX = src.x + (srcVx / srcVLen) * BADGE_OFFSET;
            const srcBadgeY = src.y + (srcVy / srcVLen) * BADGE_OFFSET;
            const tgtBadgeX = tgt.x - (tgtVx / tgtVLen) * BADGE_OFFSET;
            const tgtBadgeY = tgt.y - (tgtVy / tgtVLen) * BADGE_OFFSET;

            return (
              <g key={link.id} onClick={e => handleLinkClick(e, link.id)}
                style={{ cursor: mode === 'select' ? 'pointer' : 'default' }}>
                <path d={pathD} stroke="transparent" strokeWidth={20} fill="none" />
                <path d={pathD} stroke={isSelected ? '#0D9488' : color} strokeWidth={isSelected ? sw + 2 : sw}
                  fill="none" strokeLinecap="round" strokeLinejoin="round" strokeDasharray={isSelected ? '8,4' : 'none'} />
                <polygon points={arrowPts} fill={isSelected ? '#0D9488' : color} />
                {renderFlowBoxes(link, src, tgt, color, isSelected)}
                {renderLinkGateBadge(link, src, srcBadgeX, srcBadgeY)}
                {renderLinkGateBadge(link, tgt, tgtBadgeX, tgtBadgeY)}
                {(mode === 'node' || (mode === 'select' && isSelected)) && (
                  <g onClick={e => { e.stopPropagation(); deleteLink(link.id); }} style={{ cursor: 'pointer' }}>
                    <circle cx={midLabelX + 36} cy={midLabelY - 24} r={8} fill="#FEE2E2" stroke="#FCA5A5" strokeWidth={1} />
                    <text x={midLabelX + 36} y={midLabelY - 20} textAnchor="middle"
                      style={{ fontSize: 10, fill: '#991B1B', fontFamily: 'Arial', pointerEvents: 'none' }}>✕</text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Nodes */}
          {nodes.map(node => {
            const isSelected = selectedNode === node.id;
            const isLinkStart = linkStart === node.id;
            return (
              <g key={node.id}
                onMouseDown={e => handleNodeMouseDown(e, node.id)}
                onClick={e => handleNodeClick(e, node.id)}
                style={{ cursor: mode === 'node' || mode === 'select' ? 'move' : mode === 'link' ? 'pointer' : 'default' }}>

                {/* Link mode rings */}
                {mode === 'link' && isLinkStart && (
                  <circle cx={node.x} cy={node.y} r={NODE_RADIUS + 6} fill="none" stroke="#0D9488" strokeWidth={2} strokeDasharray="4,3" />
                )}
                {mode === 'link' && !isLinkStart && (
                  <circle cx={node.x} cy={node.y} r={NODE_RADIUS + 6} fill="none" stroke="#7B4DBF" strokeWidth={1} strokeDasharray="4,3" opacity={0.4} />
                )}
                {isSelected && (
                  <circle cx={node.x} cy={node.y} r={NODE_RADIUS + 8} fill="none" stroke="#0D9488" strokeWidth={2} />
                )}
                {/* Highlight the junction whose matrix is open in the side panel */}
                {showMatrices && selectedMatrixNode === node.id && (
                  <circle cx={node.x} cy={node.y} r={NODE_RADIUS + 12} fill="none" stroke="#E85D00" strokeWidth={3} strokeDasharray="6,3" />
                )}

                {renderNodeShape(node, isSelected, isLinkStart)}

                {node.mapLat && (
                  <>
                    <circle cx={node.x + NODE_RADIUS} cy={node.y - NODE_RADIUS} r={5} fill="#E85D00" stroke="white" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
                    {node.gates?.length > 0 && <text x={node.x + NODE_RADIUS + 8} y={node.y - NODE_RADIUS + 4} style={{ fontSize: 7, fill: '#E85D00', fontFamily: 'Arial', pointerEvents: 'none' }}>{node.gates.length} gates</text>}
                  </>
                )}

                {/* Delete + mark-as-site buttons — in Add-Junction mode for all
                    nodes, and in Select mode for the currently-selected node. */}
                {(mode === 'node' || (mode === 'select' && isSelected)) && (
                  <>
                    <g onClick={e => { e.stopPropagation(); deleteNode(node.id); }} style={{ cursor: 'pointer' }}>
                      <circle cx={node.x + NODE_RADIUS + 2} cy={node.y - NODE_RADIUS - 2} r={7} fill="#FEE2E2" stroke="#FCA5A5" strokeWidth={1} />
                      <text x={node.x + NODE_RADIUS + 2} y={node.y - NODE_RADIUS + 2} textAnchor="middle"
                        style={{ fontSize: 9, fill: '#991B1B', fontFamily: 'Arial', pointerEvents: 'none' }}>✕</text>
                    </g>
                    {node.id !== siteNodeId && !node.isTerminus && (
                      <g onClick={e => { e.stopPropagation(); markAsSite(node.id); }} style={{ cursor: 'pointer' }}>
                        <circle cx={node.x - NODE_RADIUS - 2} cy={node.y - NODE_RADIUS - 2} r={7} fill="#EDE5F8" stroke="#7B6A96" strokeWidth={1} />
                        <text x={node.x - NODE_RADIUS - 2} y={node.y - NODE_RADIUS + 2} textAnchor="middle"
                          style={{ fontSize: 8, fill: '#3D1A6E', fontFamily: 'Arial', pointerEvents: 'none' }}>S</text>
                      </g>
                    )}
                  </>
                )}
              </g>
            );
          })}

          {renderLinkGatePicker()}
        </g>
        </svg>

        {/* Right panel */}
        <div style={{ width: showMatrices ? 400 : 260, borderLeft: '1px solid #EDE5F8', overflowY: 'auto', background: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>

          {/* Junction Matrices Panel */}
          {showMatrices && (() => {
            // Escape one CSV cell (quote if it contains a comma, quote, or newline).
            const esc = (v) => {
              const s = String(v ?? '');
              return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
            };
            // Build one junction's CSV block: a small compass "diagram" of the
            // junction on the LEFT (a 3x3 grid of cells, each arm placed in the
            // compass octant of its schematic bearing and labelled with its
            // letter + road name), then a spacer column, then the turning
            // matrix on the right. Opens in Excel as a labelled mini-diagram
            // beside the matrix.
            const buildMatrixCsvBlock = (nodeId, nodeLabel) => {
              const result = getMatrix(nodeId);
              if (!result) return null;
              const { arms, matrix } = result;
              const armNames = arms.map(a => a.name).sort(sortArm);

              // 3x3 compass grid; centre cell is the junction name.
              const grid = [['', '', ''], ['', '', ''], ['', '', '']];
              grid[1][1] = nodeLabel;
              // octant (0=N,1=NE,2=E,3=SE,4=S,5=SW,6=W,7=NW) -> [row,col]
              const octantCell = { 0: [0, 1], 1: [0, 2], 2: [1, 2], 3: [2, 2], 4: [2, 1], 5: [2, 0], 6: [1, 0], 7: [0, 0] };
              for (const a of arms) {
                const oct = Math.round((a.schemBearing || 0) / 45) % 8;
                const [r, c] = octantCell[oct];
                const road = links.find(l => l.id === a.linkId)?.roadName;
                const label = road ? `${a.name}: ${road}` : a.name;
                grid[r][c] = grid[r][c] ? `${grid[r][c]} / ${label}` : label;
              }

              // Matrix rows: header, one per arm, then a totals row.
              const matrixRows = [['From \\ To', ...armNames, 'Total']];
              for (const fromName of armNames) {
                const vals = armNames.map(toName => matrix[fromName][toName] || 0);
                matrixRows.push([fromName, ...vals, vals.reduce((s, v) => s + v, 0)]);
              }
              const colTotals = armNames.map(toName => armNames.reduce((s, fromName) => s + (matrix[fromName][toName] || 0), 0));
              matrixRows.push(['Total', ...colTotals, colTotals.reduce((s, v) => s + v, 0)]);

              const lines = [esc(`Junction: ${nodeLabel}`)];
              const rowCount = Math.max(grid.length, matrixRows.length);
              for (let i = 0; i < rowCount; i++) {
                const left = i < grid.length ? grid[i] : ['', '', ''];
                const right = i < matrixRows.length ? matrixRows[i] : [];
                lines.push([...left, '', ...right].map(esc).join(','));
              }
              return lines.join('\n');
            };

            const downloadCsv = (csv, filename) => {
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = filename;
              a.click();
              URL.revokeObjectURL(url);
            };

            const exportCSV = (nodeId, nodeLabel) => {
              const block = buildMatrixCsvBlock(nodeId, nodeLabel);
              if (!block) return;
              downloadCsv(block, `junction_matrix_${nodeLabel.replace(/\s+/g, '_')}.csv`);
            };

            const exportAllCSV = () => {
              const blocks = [];
              for (const node of nodes) {
                if (node.isTerminus) continue;
                const block = buildMatrixCsvBlock(node.id, getJunctionLabel(node));
                if (block) blocks.push(block);
              }
              downloadCsv(blocks.join('\n\n'), 'all_junction_matrices.csv');
            };

            // (drawJunctionDiagramPng and sortArm are defined at component
            // scope so the PDF-export packager can reuse them.)

            // Excel sheet-name rules: <=31 chars, none of \ / * ? : [ ], and
            // unique within the workbook. usedNames dedupes across an all-
            // junctions export by appending a counter on collision.
            const sanitizeSheet = (label) => (label || 'Junction').replace(/[\\/*?:[\]]/g, '').trim().slice(0, 31) || 'Junction';
            // Rows one junction block occupies when stacked in a single sheet:
            // the 300px diagram image is ~20 rows tall, so this leaves room for
            // it plus a gap and a dividing line before the next junction.
            const BLOCK_ROWS = 24;
            const START_COL = 8; // leave columns A–G for the diagram image

            // Write one junction — title, diagram picture (cols A–G), and a
            // formatted turning matrix (from START_COL) — into ws starting at
            // 1-indexed startRow. Returns the arm count written, 0 if the
            // junction has no matrix. Shared by single- and all-junction export.
            const writeJunctionBlock = (ws, wb, nodeId, nodeLabel, startRow) => {
              const result = getMatrix(nodeId);
              if (!result) return 0;
              const { matrix } = result;
              const armNames = result.arms.map(a => a.name).sort(sortArm);
              const png = drawJunctionDiagramPng(nodeId, nodeLabel);
              if (png) {
                const imageId = wb.addImage({ base64: png.replace(/^data:image\/png;base64,/, ''), extension: 'png' });
                ws.addImage(imageId, { tl: { col: 0, row: startRow }, ext: { width: 300, height: 300 } });
              }
              const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
              const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F0FA' } };
              const writeRow = (r, cells, { bold = false, fill = false } = {}) => {
                cells.forEach((v, i) => {
                  const c = ws.getCell(r, START_COL + i);
                  c.value = v;
                  c.border = border;
                  c.alignment = { horizontal: 'center' };
                  if (bold) c.font = { bold: true };
                  if (fill) c.fill = headerFill;
                });
              };
              ws.getCell(startRow, START_COL).value = `Junction: ${nodeLabel}`;
              ws.getCell(startRow, START_COL).font = { bold: true, size: 12 };
              let r = startRow + 2;
              writeRow(r++, ['From \\ To', ...armNames, 'Total'], { bold: true, fill: true });
              for (const fromName of armNames) {
                const vals = armNames.map(t => matrix[fromName][t] || 0);
                writeRow(r, [fromName, ...vals, vals.reduce((s, v) => s + v, 0)]);
                ws.getCell(r, START_COL).font = { bold: true };
                ws.getCell(r, START_COL).fill = headerFill;
                r++;
              }
              const colTotals = armNames.map(t => armNames.reduce((s, f) => s + (matrix[f][t] || 0), 0));
              writeRow(r, ['Total', ...colTotals, colTotals.reduce((s, v) => s + v, 0)], { bold: true });
              return armNames.length;
            };

            const downloadXlsx = async (wb, filename) => {
              const buf = await wb.xlsx.writeBuffer();
              const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = filename;
              a.click();
              URL.revokeObjectURL(url);
            };

            // Export one junction as .xlsx (diagram + matrix). exceljs is
            // lazy-loaded so it stays out of the main bundle.
            const exportXLSX = async (nodeId, nodeLabel) => {
              const mod = await import('exceljs');
              const ExcelJS = mod.default || mod;
              const wb = new ExcelJS.Workbook();
              const ws = wb.addWorksheet(sanitizeSheet(nodeLabel));
              if (!writeJunctionBlock(ws, wb, nodeId, nodeLabel, 1)) return;
              await downloadXlsx(wb, `junction_matrix_${(nodeLabel || 'junction').replace(/\s+/g, '_')}.xlsx`);
            };

            // Export every junction into ONE sheet, stacked vertically with a
            // dividing line between each (rather than one tab per junction).
            const exportAllXLSX = async () => {
              const mod = await import('exceljs');
              const ExcelJS = mod.default || mod;
              const wb = new ExcelJS.Workbook();
              const ws = wb.addWorksheet('All junctions');
              let idx = 0;
              for (const node of nodes) {
                if (node.isTerminus) continue;
                const startRow = 1 + idx * BLOCK_ROWS;
                const armCount = writeJunctionBlock(ws, wb, node.id, getJunctionLabel(node), startRow);
                if (!armCount) continue;
                // Dividing line across the block width (diagram + matrix).
                const dividerRow = startRow + BLOCK_ROWS - 2;
                const lastCol = START_COL + armCount + 1;
                for (let c = 1; c <= lastCol; c++) {
                  ws.getCell(dividerRow, c).border = { bottom: { style: 'medium', color: { argb: 'FF7B4DBF' } } };
                }
                idx++;
              }
              if (idx === 0) return;
              await downloadXlsx(wb, 'all_junction_matrices.xlsx');
            };

            const matrixNode = selectedMatrixNode ? nodes.find(n => n.id === selectedMatrixNode) : null;
            const matrixResult = matrixNode ? getMatrix(matrixNode.id) : null;

            return (
              <div style={{ padding: 14, borderBottom: '1px solid #EDE5F8' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#3D1A6E', marginBottom: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Junction Matrices</div>

                {/* Junction list */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: '#6B6880', marginBottom: 6 }}>Select a junction:</div>
                  {nodes.filter(n => !n.isTerminus && links.some(l => l.source === n.id || l.target === n.id)).map(node => (
                    <div key={node.id} onClick={() => setSelectedMatrixNode(node.id === selectedMatrixNode ? null : node.id)}
                      style={{ padding: '6px 8px', borderRadius: 6, marginBottom: 3, cursor: 'pointer', background: selectedMatrixNode === node.id ? '#3D1A6E' : '#F5F0FA', color: selectedMatrixNode === node.id ? 'white' : '#3D1A6E', fontSize: 11, fontWeight: 600 }}>
                      {getJunctionLabel(node)}
                      <span style={{ fontSize: 10, fontWeight: 400, marginLeft: 6, opacity: 0.7 }}>
                        {links.filter(l => l.source === node.id || l.target === node.id).length} arms
                      </span>
                    </div>
                  ))}
                </div>

                {/* Matrix display */}
                {matrixNode && matrixResult && (() => {
                  const { arms, matrix, matrixFlags } = matrixResult;
                  const armNames = arms.map(a => a.name).sort((a, b) => {
                    // Gate letters (A, B, C…) sort alphabetically; unmapped
                    // fallback "Arm N" names sort numerically.
                    const numA = a.startsWith('Arm ') ? parseInt(a.slice(4)) : null;
                    const numB = b.startsWith('Arm ') ? parseInt(b.slice(4)) : null;
                    if (numA !== null && numB !== null) return numA - numB;
                    return a.localeCompare(b);
                  });
                  // Arms with a link but no gate matched to them. Only
                  // meaningful once the junction is mapped — before that every
                  // arm is unmatched by definition (proportional-estimate
                  // mode). Catches the easy-to-miss case on 5+ arm junctions
                  // where a gate wasn't drawn for every approach, so an arm
                  // silently carries no crossing data.
                  const unmappedArms = matrixResult.hasMapCoords ? arms.filter(a => !a.gateId).map(a => a.name) : [];
                  return (
                    <div>
                      {/* Junction name — writes node.label, the same field the
                          schematic label and "Junction N" numbering read. */}
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: '#6B6880', marginBottom: 3 }}>Junction name</label>
                      <input value={matrixNode.label || ''} onChange={e => updateNodeLabel(matrixNode.id, e.target.value)}
                        placeholder={getJunctionLabel(matrixNode)}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid #EDE5F8', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10 }} />

                      <div style={{ fontSize: 12, fontWeight: 700, color: '#3D1A6E', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {getJunctionLabel(matrixNode)} — AM Peak turning movements
                      </div>

                      {/* Per-arm road names — each arm maps to one link, so this
                          writes that link's roadName (also shown on the schematic
                          and used to label the arms in the diagram export). */}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#6B6880', marginBottom: 4 }}>Arm road names</div>
                        {arms.map(a => (
                          <div key={a.linkId} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{ width: 22, textAlign: 'center', fontWeight: 700, color: '#3D1A6E', fontSize: 11, flexShrink: 0 }}>{a.name}</span>
                            <input value={links.find(l => l.id === a.linkId)?.roadName || ''}
                              onChange={e => setLinks(prev => prev.map(l => l.id === a.linkId ? { ...l, roadName: e.target.value } : l))}
                              placeholder="Road name"
                              style={{ flex: 1, padding: '4px 6px', borderRadius: 5, border: '1.5px solid #EDE5F8', fontSize: 11, fontFamily: 'inherit', boxSizing: 'border-box', minWidth: 0 }} />
                          </div>
                        ))}
                      </div>

                      <div style={{ overflowX: 'auto', marginBottom: 10 }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
                          <thead>
                            <tr>
                              <th style={{ padding: '8px 11px', background: '#F5F0FA', color: '#6B6880', fontWeight: 700, textAlign: 'left', border: '1px solid #EDE5F8' }}>From ↓ To →</th>
                              {armNames.map(name => (
                                <th key={name} style={{ padding: '8px 11px', background: '#F5F0FA', color: '#3D1A6E', fontWeight: 700, textAlign: 'center', border: '1px solid #EDE5F8', whiteSpace: 'nowrap' }}>{name}</th>
                              ))}
                              <th style={{ padding: '8px 11px', background: '#1C1C2E', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #EDE5F8', whiteSpace: 'nowrap' }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {armNames.map(fromName => {
                              const rowTotal = armNames.reduce((sum, toName) => sum + (matrix[fromName][toName] || 0), 0);
                              return (
                                <tr key={fromName}>
                                  <td style={{ padding: '8px 11px', background: '#F5F0FA', color: '#3D1A6E', fontWeight: 700, border: '1px solid #EDE5F8', whiteSpace: 'nowrap' }}>{fromName}</td>
                                  {armNames.map(toName => {
                                    const val = matrix[fromName][toName];
                                    const isDiag = fromName === toName;
                                    const isHigh = typeof val === 'number' && val > 10;
                                    const isUturn = isDiag && val > 0;
                                    const isFlagged = !!matrixFlags?.[fromName]?.[toName];
                                    return (
                                      <td key={toName} title={isFlagged ? 'A route crossed a one-way gate against its declared direction — worth double-checking gate placement' : undefined}
                                        style={{ padding: '8px 11px', textAlign: 'center', border: isFlagged ? '1px solid #F59E0B' : '1px solid #EDE5F8', background: isUturn ? '#FFF5EE' : isDiag ? '#F9F7FD' : isHigh ? '#EDE5F8' : 'white', color: isUturn ? '#E85D00' : isDiag ? '#C4BDD4' : isHigh ? '#3D1A6E' : '#1C1C2E', fontWeight: isHigh || isUturn ? 700 : 400 }}>
                                        {val === 0 && isDiag ? '—' : val}{isFlagged ? ' ⚠️' : ''}
                                      </td>
                                    );
                                  })}
                                  <td style={{ padding: '8px 11px', textAlign: 'center', border: '1px solid #EDE5F8', background: '#1C1C2E', color: 'white', fontWeight: 700 }}>{rowTotal}</td>
                                </tr>
                              );
                            })}
                            {/* Total row */}
                            <tr>
                              <td style={{ padding: '8px 11px', background: '#1C1C2E', color: 'white', fontWeight: 700, border: '1px solid #EDE5F8', whiteSpace: 'nowrap' }}>Total</td>
                              {armNames.map(toName => {
                                const colTotal = armNames.reduce((sum, fromName) => sum + (matrix[fromName][toName] || 0), 0);
                                return (
                                  <td key={toName} style={{ padding: '8px 11px', textAlign: 'center', border: '1px solid #EDE5F8', background: '#1C1C2E', color: 'white', fontWeight: 700 }}>{colTotal}</td>
                                );
                              })}
                              <td style={{ padding: '8px 11px', textAlign: 'center', border: '1px solid #EDE5F8', background: '#3D1A6E', color: 'white', fontWeight: 700 }}>
                                {armNames.reduce((sum, fromName) => sum + armNames.reduce((s, toName) => s + (matrix[fromName][toName] || 0), 0), 0)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div style={{ fontSize: 10, color: '#6B6880', marginBottom: 8, background: matrixResult.hasRouteData ? '#F0FDF9' : '#F5F0FA', padding: '6px 8px', borderRadius: 5 }}>
                        {matrixResult.hasRouteData
                          ? '✅ Turning movements from HERE routes via gate matching'
                          : matrixResult.hasMapCoords
                          ? 'ℹ️ Junction mapped but no routes cross any gate — check gate placement'
                          : 'ℹ️ Proportional estimate — use 🎯 Map Junction to enable gate matching'}
                      </div>
                      {unmappedArms.length > 0 && (
                        <div style={{ fontSize: 10, color: '#92400E', background: '#FFF9E6', border: '1px solid #F59E0B', padding: '6px 8px', borderRadius: 5, marginBottom: 8 }}>
                          ⚠️ {unmappedArms.length} arm{unmappedArms.length === 1 ? '' : 's'} ({unmappedArms.join(', ')}) {unmappedArms.length === 1 ? "isn't" : "aren't"} matched to a gate — carry no turning data. Draw a gate for {unmappedArms.length === 1 ? 'it' : 'each'}, or use a link's gate badge to reassign.
                        </div>
                      )}
                      <button onClick={() => exportXLSX(matrixNode.id, getJunctionLabel(matrixNode)).catch(err => console.error('xlsx export failed', err))}
                        style={{ width: '100%', padding: '7px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 6 }}>
                        📊 Export this junction (Excel + diagram)
                      </button>
                      <button onClick={() => exportAllXLSX().catch(err => console.error('xlsx export failed', err))}
                        style={{ width: '100%', padding: '7px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 10 }}>
                        📊 Export all junctions (Excel + diagrams)
                      </button>
                      <button onClick={() => exportCSV(matrixNode.id, getJunctionLabel(matrixNode))}
                        style={{ width: '100%', padding: '7px', background: 'white', color: '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 6 }}>
                        ⬇️ Export this matrix (CSV)
                      </button>
                      <button onClick={exportAllCSV}
                        style={{ width: '100%', padding: '7px', background: 'white', color: '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        ⬇️ Export all junctions (CSV)
                      </button>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {/* Link editor */}
          {editingLink && editingLinkData && (
            <div style={{ padding: 14, borderBottom: '1px solid #EDE5F8' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#3D1A6E', marginBottom: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Edit Link Flows</div>

              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: '#6B6880', marginBottom: 3 }}>Road Name</label>
                <input value={editingLinkData.roadName || ''} onChange={e => setLinks(prev => prev.map(l => l.id === editingLink ? { ...l, roadName: e.target.value } : l))}
                  placeholder="e.g. High Street"
                  style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid #EDE5F8', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }} />
              </div>

              {/* Development flows (auto from assignment) */}
              <div style={{ fontSize: 10, fontWeight: 700, color: '#3D1A6E', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Development Flows</div>

              <div style={{ background: '#F5F0FA', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#3D1A6E', marginBottom: 6 }}>AM Peak</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 9, color: '#6B6880', marginBottom: 2 }}>Flow →</label>
                    <input type="number" value={editingLinkData.arrivals ?? ''}
                      onChange={e => updateLinkFlows(editingLink, 'arrivals', e.target.value)}
                      placeholder={amVehicle ? `~${amVehicle}` : '0'}
                      style={{ width: '100%', padding: '5px 6px', borderRadius: 5, border: '1.5px solid #EDE5F8', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 9, color: '#6B6880', marginBottom: 2 }}>Flow ←</label>
                    <input type="number" value={editingLinkData.departures ?? ''}
                      onChange={e => updateLinkFlows(editingLink, 'departures', e.target.value)}
                      placeholder={amVehicle ? `~${amVehicle}` : '0'}
                      style={{ width: '100%', padding: '5px 6px', borderRadius: 5, border: '1.5px solid #EDE5F8', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>

              <div style={{ background: '#F0FDF9', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#0D9488', marginBottom: 6 }}>PM Peak</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 9, color: '#6B6880', marginBottom: 2 }}>Flow →</label>
                    <input type="number" value={editingLinkData.pmArrivals ?? ''}
                      onChange={e => setLinks(prev => prev.map(l => l.id === editingLink ? { ...l, pmArrivals: e.target.value === '' ? null : parseInt(e.target.value) || null } : l))}
                      placeholder={pmVehicle ? `~${pmVehicle}` : '0'}
                      style={{ width: '100%', padding: '5px 6px', borderRadius: 5, border: '1.5px solid #B2DFDB', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 9, color: '#6B6880', marginBottom: 2 }}>Flow ←</label>
                    <input type="number" value={editingLinkData.pmDepartures ?? ''}
                      onChange={e => setLinks(prev => prev.map(l => l.id === editingLink ? { ...l, pmDepartures: e.target.value === '' ? null : parseInt(e.target.value) || null } : l))}
                      placeholder={pmVehicle ? `~${pmVehicle}` : '0'}
                      style={{ width: '100%', padding: '5px 6px', borderRadius: 5, border: '1.5px solid #B2DFDB', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>

              {/* Baseline flows */}
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6B6880', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Baseline Survey Flows</div>

              <div style={{ background: '#FFF9E6', borderRadius: 6, padding: '8px 10px', marginBottom: 6, border: '1px solid #FFD000' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>AM Peak</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 9, color: '#6B6880', marginBottom: 2 }}>Flow →</label>
                    <input type="number" value={editingLinkData.baselineAmArrivals ?? ''}
                      onChange={e => setLinks(prev => prev.map(l => l.id === editingLink ? { ...l, baselineAmArrivals: e.target.value === '' ? null : parseInt(e.target.value) || null } : l))}
                      placeholder="0"
                      style={{ width: '100%', padding: '5px 6px', borderRadius: 5, border: '1.5px solid #FFD000', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 9, color: '#6B6880', marginBottom: 2 }}>Flow ←</label>
                    <input type="number" value={editingLinkData.baselineAmDepartures ?? ''}
                      onChange={e => setLinks(prev => prev.map(l => l.id === editingLink ? { ...l, baselineAmDepartures: e.target.value === '' ? null : parseInt(e.target.value) || null } : l))}
                      placeholder="0"
                      style={{ width: '100%', padding: '5px 6px', borderRadius: 5, border: '1.5px solid #FFD000', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>

              <div style={{ background: '#FFF9E6', borderRadius: 6, padding: '8px 10px', marginBottom: 10, border: '1px solid #FFD000' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>PM Peak</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 9, color: '#6B6880', marginBottom: 2 }}>Flow →</label>
                    <input type="number" value={editingLinkData.baselinePmArrivals ?? ''}
                      onChange={e => setLinks(prev => prev.map(l => l.id === editingLink ? { ...l, baselinePmArrivals: e.target.value === '' ? null : parseInt(e.target.value) || null } : l))}
                      placeholder="0"
                      style={{ width: '100%', padding: '5px 6px', borderRadius: 5, border: '1.5px solid #FFD000', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 9, color: '#6B6880', marginBottom: 2 }}>Flow ←</label>
                    <input type="number" value={editingLinkData.baselinePmDepartures ?? ''}
                      onChange={e => setLinks(prev => prev.map(l => l.id === editingLink ? { ...l, baselinePmDepartures: e.target.value === '' ? null : parseInt(e.target.value) || null } : l))}
                      placeholder="0"
                      style={{ width: '100%', padding: '5px 6px', borderRadius: 5, border: '1.5px solid #FFD000', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>

              <button
                onClick={() => setLinks(prev => prev.map(l => l.id === editingLink ? { ...l, invertFlows: !l.invertFlows } : l))}
                style={{ width: '100%', padding: '6px', background: editingLinkData.invertFlows ? '#0D9488' : 'white', color: editingLinkData.invertFlows ? 'white' : '#0D9488', border: '1.5px solid #0D9488', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                {editingLinkData.invertFlows ? '✅ Flow sides swapped' : '⇄ Swap flow sides'}
              </button>

              <button onClick={() => { setEditingLink(null); setSelectedLink(null); }}
                style={{ width: '100%', padding: '6px', background: '#3D1A6E', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 6 }}>
                Done
              </button>
              <button onClick={() => deleteLink(editingLink)}
                style={{ width: '100%', padding: '6px', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Delete Link
              </button>
            </div>
          )}

          {/* Node editor */}
          {selectedNode && selectedNodeData && !editingLink && (
            <div style={{ padding: 14, borderBottom: '1px solid #EDE5F8' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#3D1A6E', marginBottom: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Edit Junction</div>

              <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: '#6B6880', marginBottom: 3 }}>Label</label>
              <input value={selectedNodeData.label || ''}
                onChange={e => updateNodeLabel(selectedNode, e.target.value)}
                placeholder="e.g. A329"
                style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid #EDE5F8', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10 }} />

              <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: '#6B6880', marginBottom: 6 }}>Junction Type</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: 10 }}>
                {JUNCTION_TYPES.map(jt => (
                  <button key={jt.key} onClick={() => updateNodeType(selectedNode, jt.key)}
                    style={{
                      padding: '6px 4px',
                      background: (selectedNodeData.junctionType || 'priority') === jt.key ? '#3D1A6E' : 'white',
                      color: (selectedNodeData.junctionType || 'priority') === jt.key ? 'white' : '#3D1A6E',
                      border: '1.5px solid',
                      borderColor: (selectedNodeData.junctionType || 'priority') === jt.key ? '#3D1A6E' : '#EDE5F8',
                      borderRadius: 5,
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      textAlign: 'center',
                    }}>
                    {jt.label}
                  </button>
                ))}
              </div>

              {selectedNodeData.mapLat && (
                <div style={{ fontSize: 10, color: '#E85D00', background: '#FFF5EE', padding: '5px 8px', borderRadius: 5, marginBottom: 8, border: '1px solid #E85D00' }}>
                  🎯 Mapped: {selectedNodeData.mapLat.toFixed(5)}, {selectedNodeData.mapLng.toFixed(5)}
                  {selectedNodeData.gates?.length > 0 && <span style={{ marginLeft: 6, fontWeight: 700 }}>· {selectedNodeData.gates.length} gates</span>}
                  <button onClick={() => setNodes(prev => prev.map(n => n.id === selectedNode ? { ...n, mapLat: null, mapLng: null, gates: null, linkGateMap: null } : n))}
                    style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#C4BDD4', fontFamily: 'inherit' }}>Remove</button>
                </div>
              )}
              {selectedNodeData.id !== siteNodeId && (
                <button onClick={() => markAsSite(selectedNode)}
                  style={{ width: '100%', padding: '6px', background: '#3D1A6E', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 6 }}>
                  Mark as Site Access
                </button>
              )}
              <button onClick={() => deleteNode(selectedNode)}
                style={{ width: '100%', padding: '6px', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Delete Junction
              </button>
            </div>
          )}

          {/* Legend */}
          {!editingLink && !selectedNode && (
            <div style={{ padding: 14, flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#3D1A6E', marginBottom: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Junction Types</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width={28} height={28}><rect x={4} y={4} width={20} height={20} rx={2} fill="white" stroke="#7B6A96" strokeWidth={2} /></svg>
                  <span style={{ fontSize: 11, color: '#6B6880' }}>Priority junction</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width={28} height={28}><circle cx={14} cy={14} r={10} fill="white" stroke="#7B6A96" strokeWidth={2} /><circle cx={14} cy={14} r={4.5} fill="#7B6A96" opacity={0.3} /></svg>
                  <span style={{ fontSize: 11, color: '#6B6880' }}>Roundabout</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width={28} height={28}>
                    <rect x={4} y={4} width={20} height={20} rx={2} fill="white" stroke="#7B6A96" strokeWidth={2} />
                    <circle cx={14} cy={8} r={3} fill="#DC2626" />
                    <circle cx={14} cy={14} r={3} fill="#F59E0B" />
                    <circle cx={14} cy={20} r={3} fill="#16A34A" />
                  </svg>
                  <span style={{ fontSize: 11, color: '#6B6880' }}>Signalised junction</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width={28} height={28}>
                    <circle cx={14} cy={14} r={10} fill="white" stroke="#7B6A96" strokeWidth={2} />
                    <circle cx={14} cy={14} r={4.5} fill="#7B6A96" opacity={0.15} />
                    <circle cx={14} cy={9} r={2.5} fill="#DC2626" />
                    <circle cx={14} cy={14} r={2.5} fill="#F59E0B" />
                    <circle cx={14} cy={19} r={2.5} fill="#16A34A" />
                  </svg>
                  <span style={{ fontSize: 11, color: '#6B6880' }}>Signalised roundabout</span>
                </div>
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: '#3D1A6E', marginBottom: 8, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Flow Boxes</div>
              <div style={{ fontSize: 11, color: '#6B6880', lineHeight: 1.6 }}>
                <div style={{ marginBottom: 4 }}>Each link shows <strong>AM | PM</strong> flow in each direction of travel — the letters (e.g. WB, EB) show which way</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 2, background: '#EDE5F8', border: '1.5px solid #7B4DBF' }} />
                  <span>Near side</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 2, background: '#F0FDF9', border: '1.5px solid #0D9488' }} />
                  <span>Far side</span>
                </div>
              </div>

              <div style={{ marginTop: 14, fontSize: 11, fontWeight: 700, color: '#3D1A6E', marginBottom: 8, letterSpacing: '0.08em', textTransform: 'uppercase' }}>How to use</div>
              <div style={{ fontSize: 11, color: '#6B6880', lineHeight: 1.6 }}>
                <div style={{ marginBottom: 4 }}>✦ <strong>Select / Edit</strong> — click a junction or link to edit it; drag to move; on the selected item, ✕ deletes and S marks a junction as site</div>
                <div style={{ marginBottom: 4 }}>⬡ <strong>Add Junction</strong> — click canvas to place; click a link to split it</div>
                <div style={{ marginBottom: 4 }}>— <strong>Add Link</strong> — click two junctions to connect (or empty space for a dead-end)</div>
                <div style={{ marginBottom: 4 }}>🎯 <strong>Map Junction</strong> — draw gates to enable turning matrices and automatic link flows</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}